/*
  Earthonmall Page Window Manager

  Final page-window coordination version.

  Responsibilities:
  - Discover all section-level virtualized sections.
  - Apply a section priority model.
  - Log lifecycle movement and state changes.
  - Coordinate hydration order and active-window limits.
  - Keep static/copy/SEO-preserved sections out of manager control.

  Required load order:
  1. eom-virtual-sections.js
  2. window.EOMPageWindowManagerConfig
  3. eom-page-window-manager.js
*/

(() => {
  if (window.EOMPageWindowManager) return;

  const DEFAULT_CONFIG = {
    enabled: true,
    debug: false,
    homepageOnly: true,
    selector: "[data-eom-virtual-section]",
    controlActivation: true,
    initialLoadReduction: true,
    strongCleanup: true,
    imageMemoryCleanup: false,
    scrollIntegrity: {
      enabled: false,
      monitorOnly: true
    },
    adaptiveBehavior: true,
    memoryPressureMode: true,
    chunkPlaceholderPreview: false,
    chunkPlaceholderPreviewInEditor: true,
    homeChunkFetchPoc: {
      enabled: false,
      autoStart: false,
      targetChunkId: "home-close",
      url: "",
      mountHidden: true
    },
    homeChunkFetchManager: {
      enabled: true,
      autoStart: true,
      mobileOnly: true,
      url: "",
      mountHidden: true,
      fetchAheadViewports: 4,
      hydrateAheadViewports: 3,
      offloadViewports: 4,
      restoreBehindViewports: 1.5,
      compactAheadViewports: 5.5,
      maxConcurrent: 1,
      cache: true,
      skipChunkIds: ["home-initial"]
    },
    homeChunkHeightReservation: {
      enabled: true,
      mobileOnly: true,
      minHeight: 360,
      correctionTolerance: 32,
      compensateFarAbove: false,
      roleEstimates: {
        "card-grid": 5200,
        value: 2600,
        bundle: 3000,
        education: 2400,
        close: 1500,
        visual: 1800
      }
    },
    seoNoJsMinimums: {
      h1: 1,
      crawlableTextChars: 1200,
      crawlableLinks: 12,
      majorCollectionLinks: 3,
      importantInternalLinks: 8,
      schemaScripts: 1
    },
    mobileInitialHtmlReduction: {
      enabled: true,
      maxWidth: 749,
      lowerChunkMinOrder: 20,
      protectChunkIds: ["home-initial"],
      domNodeBudget: 3600,
      activeSectionBudget: 5
    },
    relaxInShopifyEditor: true,
    hydrateOnUserIntent: true,
    lifecycleLogLimit: 240,
    layoutShiftTolerance: 32,
    managerOverheadBudgetPercent: 10,
    backToTopSelector: 'a[href="#top"], a[href="#MainContent"], [data-eom-back-to-top], [data-back-to-top]',

    viewportWindow: {
      ahead: 1.25,
      behind: 0.65
    },

    preloadDistance: 1050,
    offloadDistance: 900,
    maxActiveManagedSections: 4,
    maxOutOfViewActiveManagedSections: 1,
    maxActiveVisualSections: 2,
    maxActiveInteractiveSections: 1,

    priorityTypes: ["static", "visual", "interactive", "card-grid", "critical", "footer-static"],
    interactiveModes: ["interactive", "card-grid"],
    visualModes: ["visual", "template", "custom", "critical"],

    homeChunks: [
      {
        id: "home-initial",
        label: "Initial visible homepage",
        order: 10,
        strategy: "initial",
        role: "critical",
        fetchable: false,
        match: [
          "minimalist_hero_with_image",
          "select_a_collection",
          "eom_bulk_savings_strip",
          "format-gateway",
          "bulk-strip"
        ]
      },
      {
        id: "home-product-discovery",
        label: "Product discovery",
        order: 20,
        strategy: "future-fetch",
        role: "card-grid",
        fetchable: true,
        match: [
          "section_fast_track_matrix",
          "fast-track-matrix",
          "matrix-engine"
        ]
      },
      {
        id: "home-subscribe-value",
        label: "Subscribe and value proof",
        order: 30,
        strategy: "future-fetch",
        role: "value",
        fetchable: true,
        match: [
          "promote_subscription",
          "featured_subscription_plan",
          "nrt_price_comparison",
          "subscribe-section",
          "repeat-sub-section",
        ]
      },
      {
        id: "home-bundles",
        label: "Quiz and bundles",
        order: 40,
        strategy: "future-fetch",
        role: "bundle",
        fetchable: true,
        match: [
          "interactive_quit_quiz",
          "featured_nrt_bundle",
          "nrt_bundle_options"
        ]
      },
      {
        id: "home-education",
        label: "Brands and education",
        order: 50,
        strategy: "future-fetch",
        role: "education",
        fetchable: true,
        match: [
          "shop_by_brand_logos",
          "brand-logos",
          "featured_journey",
          "blog_section",
          "blog-intel"
        ]
      },
      {
        id: "home-close",
        label: "Close and footer-adjacent",
        order: 60,
        strategy: "future-fetch",
        role: "close",
        fetchable: true,
        match: [
          "end_of_page",
          "before-checkout",
          "eom-before-checkout",
          "footer"
        ]
      }
    ],

    perSectionOverrides: {},
    refreshDelay: 120
  };

  const state = {
    enabled: false,
    initialized: false,
    reason: "",
    sections: [],
    summary: {},
    stableMobileViewportHeight: 0,
    chunkPlaceholders: [],
    chunkFetchPoc: {
      state: "pending",
      targetChunkId: "home-close",
      url: "",
      mounted: false,
      error: "",
      requestedAt: "",
      mountedAt: "",
      restoredAt: "",
      restoredCount: 0,
      fetchedBytes: 0,
      mountedSections: 0,
      mountedNodes: 0
    },
    chunkFetchManager: {
      enabled: false,
      viewportActive: false,
      state: "idle",
      error: "",
      requestedChunks: 0,
      fetchingChunks: 0,
      fetchedChunks: 0,
      mountedChunks: 0,
      offloadedChunks: 0,
      restoredChunks: 0,
      repeatedNetworkRequests: 0,
      totalFetchedBytes: 0,
      sourceNetworkRequests: 0,
      sourceNetworkBytes: 0,
      extractedPayloadBytes: 0,
      avoidedFullPageRequests: 0,
      payloadReductionEstimatePercent: 0,
      payloadSourceState: "idle",
      payloadSourceError: "",
      totalMountedNodes: 0,
      lastActionAt: "",
      chunks: {}
    },
    chunkHeightReservations: {
      enabled: false,
      viewportActive: false,
      chunks: {},
      measuredChunks: 0,
      estimatedChunks: 0,
      stableChunks: 0,
      unstableChunks: 0,
      totalReservedHeight: 0,
      totalMeasuredHeight: 0,
      maxHeightDelta: 0,
      scrollCompensations: 0,
      scrollCompensationPixels: 0,
      activeReservationExpectedChunks: 0,
      activeReservationReleasedChunks: 0,
      appliedReservationChunks: 0,
      activeReservationExpectedChunkIds: [],
      activeReservationReleasedChunkIds: [],
      appliedReservationChunkIds: [],
      lastAdjustmentAt: ""
    },
    chunkArtifactCompaction: {
      enabled: true,
      currentCompactedSections: 0,
      currentCompactedNodes: 0,
      currentCompactedScriptStyleNodes: 0,
      totalCompactedSections: 0,
      totalCompactedNodes: 0,
      totalCompactedScriptStyleNodes: 0,
      totalRestoredSections: 0,
      totalRestoredNodes: 0,
      totalRestoredScriptStyleNodes: 0,
      lastActionAt: ""
    },
    mediaCleanup: {
      enabled: true,
      currentStrippedSections: 0,
      currentStrippedMedia: 0,
      totalStrippedSections: 0,
      totalStrippedMedia: 0,
      totalRestoredSections: 0,
      totalRestoredMedia: 0,
      protectedSections: 0,
      lastActionAt: ""
    },
    scrollAnchorRepair: {
      enabled: false,
      batches: 0,
      compensatedBatches: 0,
      compensationPixels: 0,
      upwardBatches: 0,
      upwardCompensatedBatches: 0,
      skippedBatches: 0,
      missingAnchorBatches: 0,
      lastActionAt: ""
    },
    scrollIntegrity: {
      enabled: false,
      corrections: 0,
      blockedJumps: 0,
      allowedLargeJumps: 0,
      lastTrustedScrollY: window.scrollY || 0,
      lastTrustedScrollAt: Date.now(),
      lastInputAt: 0,
      lastInputType: "",
      explicitIntentUntil: 0,
      lastDelta: 0,
      suspectedJumps: 0,
      largestBlockedDelta: 0,
      lastActionAt: ""
    },
    seoShellCompaction: {
      enabled: true,
      currentCompactedSections: 0,
      currentOriginalNodes: 0,
      currentCompactNodes: 0,
      totalCompactedSections: 0,
      totalOriginalNodes: 0,
      totalCompactNodes: 0,
      totalRestoredSections: 0,
      totalRestoredNodes: 0,
      mobileProtectedSections: 0,
      mobileProtectedCompactedSections: 0,
      lastActionAt: ""
    },
    config: {},
    lifecycle: [],
    lastRefreshAt: null,
    lastRefreshError: "",
    startupSweepComplete: false,
    lastScrollY: window.scrollY || 0,
    lastScrollAt: Date.now(),
    scrollVelocity: 0
  };

  let config = mergeConfig(DEFAULT_CONFIG, window.EOMPageWindowManagerConfig || {});
  let refreshFrame = null;
  let refreshTimer = null;
  let refreshInProgress = false;
  let previousById = new Map();
  let sectionMemory = new Map();
  const chunkPayloadCache = new Map();
  let chunkPayloadSourcePromise = null;
  const crawlableSignalCache = new WeakMap();
  const chunkArtifactCache = new WeakMap();
  const seoShellCache = new WeakMap();
  const PREVIEW_LAYER_ID = "eom-home-chunk-placeholder-preview-layer";
  const PREVIEW_STYLE_ID = "eom-home-chunk-placeholder-preview-style";
  const FETCH_POC_MOUNT_ID = "eom-home-chunk-fetch-poc-mount";
  const MOBILE_SEO_SHELL_PROTECTED_CHUNK_IDS = new Set([
    "home-close",
    "home-subscribe-value",
    "home-education"
  ]);
  const MOBILE_VISIBLE_PATH_PROTECTED_CHUNK_IDS = new Set([
    "home-subscribe-value",
    "home-education",
    "home-close"
  ]);
  const MOBILE_STABLE_VISUAL_SECTION_KINDS = new Set([]);
  const HEIGHT_RESERVATION_EXCLUDED_CHUNK_IDS = new Set([
    "home-subscribe-value"
  ]);
  let fetchPocPromise = null;
  const chunkFetchPromises = new Map();

  function isPlainObject(value) {
    return Boolean(value) && typeof value === "object" && !Array.isArray(value);
  }

  function mergeConfig(base, override) {
    const merged = { ...base, ...override };

    if (isPlainObject(base.viewportWindow) || isPlainObject(override.viewportWindow)) {
      merged.viewportWindow = {
        ...(base.viewportWindow || {}),
        ...(override.viewportWindow || {})
      };
    }

    if (isPlainObject(base.perSectionOverrides) || isPlainObject(override.perSectionOverrides)) {
      merged.perSectionOverrides = {
        ...(base.perSectionOverrides || {}),
        ...(override.perSectionOverrides || {})
      };
    }

    if (isPlainObject(base.homeChunkFetchPoc) || isPlainObject(override.homeChunkFetchPoc)) {
      merged.homeChunkFetchPoc = {
        ...(base.homeChunkFetchPoc || {}),
        ...(override.homeChunkFetchPoc || {})
      };
    }

    if (isPlainObject(base.homeChunkFetchManager) || isPlainObject(override.homeChunkFetchManager)) {
      merged.homeChunkFetchManager = {
        ...(base.homeChunkFetchManager || {}),
        ...(override.homeChunkFetchManager || {})
      };

      const skipChunkIds = override.homeChunkFetchManager?.skipChunkIds ?? base.homeChunkFetchManager?.skipChunkIds;
      merged.homeChunkFetchManager.skipChunkIds = Array.isArray(skipChunkIds)
        ? [...skipChunkIds]
        : [...DEFAULT_CONFIG.homeChunkFetchManager.skipChunkIds];
    }

    if (isPlainObject(base.homeChunkHeightReservation) || isPlainObject(override.homeChunkHeightReservation)) {
      merged.homeChunkHeightReservation = {
        ...(base.homeChunkHeightReservation || {}),
        ...(override.homeChunkHeightReservation || {}),
        roleEstimates: {
          ...(base.homeChunkHeightReservation?.roleEstimates || {}),
          ...(override.homeChunkHeightReservation?.roleEstimates || {})
        }
      };
    }

    if (isPlainObject(base.seoNoJsMinimums) || isPlainObject(override.seoNoJsMinimums)) {
      merged.seoNoJsMinimums = {
        ...(base.seoNoJsMinimums || {}),
        ...(override.seoNoJsMinimums || {})
      };
    }

    if (isPlainObject(base.mobileInitialHtmlReduction) || isPlainObject(override.mobileInitialHtmlReduction)) {
      merged.mobileInitialHtmlReduction = {
        ...(base.mobileInitialHtmlReduction || {}),
        ...(override.mobileInitialHtmlReduction || {})
      };
    }

    return merged;
  }

  function toNumber(value, fallback = 0) {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  }

  function getPublicConfig() {
    return {
      enabled: config.enabled !== false,
      debug: Boolean(config.debug),
      homepageOnly: config.homepageOnly !== false,
      selector: config.selector,
      controlActivation: config.controlActivation !== false,
      initialLoadReduction: config.initialLoadReduction !== false,
      strongCleanup: config.strongCleanup !== false,
      imageMemoryCleanup: config.imageMemoryCleanup !== false,
      adaptiveBehavior: config.adaptiveBehavior !== false,
      memoryPressureMode: config.memoryPressureMode !== false,
      chunkPlaceholderPreview: Boolean(config.chunkPlaceholderPreview),
      chunkPlaceholderPreviewInEditor: config.chunkPlaceholderPreviewInEditor !== false,
      homeChunkFetchPoc: { ...(config.homeChunkFetchPoc || {}) },
      homeChunkFetchManager: {
        ...(config.homeChunkFetchManager || {}),
        skipChunkIds: Array.isArray(config.homeChunkFetchManager?.skipChunkIds)
          ? [...config.homeChunkFetchManager.skipChunkIds]
          : [...DEFAULT_CONFIG.homeChunkFetchManager.skipChunkIds]
      },
      homeChunkHeightReservation: {
        ...(config.homeChunkHeightReservation || {}),
        roleEstimates: {
          ...(config.homeChunkHeightReservation?.roleEstimates || {})
        }
      },
      seoNoJsMinimums: { ...(config.seoNoJsMinimums || {}) },
      mobileInitialHtmlReduction: {
        ...(config.mobileInitialHtmlReduction || {}),
        protectChunkIds: Array.isArray(config.mobileInitialHtmlReduction?.protectChunkIds)
          ? [...config.mobileInitialHtmlReduction.protectChunkIds]
          : [...DEFAULT_CONFIG.mobileInitialHtmlReduction.protectChunkIds]
      },
      relaxInShopifyEditor: config.relaxInShopifyEditor !== false,
      hydrateOnUserIntent: config.hydrateOnUserIntent !== false,
      lifecycleLogLimit: toNumber(config.lifecycleLogLimit, DEFAULT_CONFIG.lifecycleLogLimit),
      layoutShiftTolerance: toNumber(config.layoutShiftTolerance, DEFAULT_CONFIG.layoutShiftTolerance),
      managerOverheadBudgetPercent: toNumber(config.managerOverheadBudgetPercent, DEFAULT_CONFIG.managerOverheadBudgetPercent),
      backToTopSelector: config.backToTopSelector,
      viewportWindow: { ...(config.viewportWindow || {}) },
      preloadDistance: toNumber(config.preloadDistance, DEFAULT_CONFIG.preloadDistance),
      offloadDistance: toNumber(config.offloadDistance, DEFAULT_CONFIG.offloadDistance),
      maxActiveManagedSections: toNumber(config.maxActiveManagedSections, DEFAULT_CONFIG.maxActiveManagedSections),
      maxOutOfViewActiveManagedSections: toNumber(config.maxOutOfViewActiveManagedSections, DEFAULT_CONFIG.maxOutOfViewActiveManagedSections),
      maxActiveVisualSections: toNumber(config.maxActiveVisualSections, DEFAULT_CONFIG.maxActiveVisualSections),
      maxActiveInteractiveSections: toNumber(config.maxActiveInteractiveSections, DEFAULT_CONFIG.maxActiveInteractiveSections),
      priorityTypes: Array.isArray(config.priorityTypes) ? [...config.priorityTypes] : [...DEFAULT_CONFIG.priorityTypes],
      interactiveModes: Array.isArray(config.interactiveModes) ? [...config.interactiveModes] : [...DEFAULT_CONFIG.interactiveModes],
      visualModes: Array.isArray(config.visualModes) ? [...config.visualModes] : [...DEFAULT_CONFIG.visualModes],
      homeChunks: Array.isArray(config.homeChunks) ? config.homeChunks.map((chunk) => ({ ...chunk })) : [],
      perSectionOverrides: { ...(config.perSectionOverrides || {}) },
      refreshDelay: toNumber(config.refreshDelay, DEFAULT_CONFIG.refreshDelay)
    };
  }

  function isHomepage() {
    const path = window.location.pathname.replace(/\/+$/, "") || "/";
    return path === "/";
  }

  function shouldRun() {
    if (config.enabled === false) {
      state.reason = "disabled by EOMPageWindowManagerConfig.enabled";
      return false;
    }

    if (config.relaxInShopifyEditor !== false && window.Shopify?.designMode) {
      state.reason = "relaxed because Shopify design mode is active";
      return false;
    }

    if (config.homepageOnly !== false && !isHomepage()) {
      state.reason = "homepageOnly is enabled and this is not the homepage";
      return false;
    }

    state.reason = "enabled";
    return true;
  }

  function logLifecycle(type, section, detail = {}) {
    const event = {
      time: new Date().toISOString(),
      type,
      index: section?.index ?? null,
      id: section?.id || "",
      name: section?.name || "",
      state: section?.state || "",
      mode: section?.mode || "",
      sectionType: section?.type || "",
      homeChunkId: section?.homeChunkId || "",
      distanceBand: section?.distanceBand || "",
      ...detail
    };

    state.lifecycle.push(event);

    const limit = toNumber(config.lifecycleLogLimit, DEFAULT_CONFIG.lifecycleLogLimit);
    if (state.lifecycle.length > limit) {
      state.lifecycle.splice(0, state.lifecycle.length - limit);
    }

    window.dispatchEvent(new CustomEvent("eom:page-window-manager-lifecycle", { detail: event }));
  }

  function getSectionId(section, index) {
    return (
      section.dataset.sectionId ||
      section.id ||
      section.dataset.eomSectionId ||
      section.dataset.shopifyEditorSection ||
      `managed-section-${index}`
    );
  }

  function getSectionName(section, index) {
    const className = String(section.className || "").replace(/\s+/g, " ").trim();
    const firstClass = className.split(" ")[0];
    return section.dataset.eomSectionName || firstClass || getSectionId(section, index);
  }

  function getSectionOverride(section, index) {
    const overrides = config.perSectionOverrides || {};
    const className = String(section.className || "").replace(/\s+/g, " ").trim();
    const firstClass = className.split(" ")[0];
    const keys = [
      section.dataset.sectionId,
      section.id,
      section.dataset.eomSectionId,
      section.dataset.eomSectionName,
      firstClass,
      `managed-section-${index}`
    ].filter(Boolean);

    for (const key of keys) {
      if (isPlainObject(overrides[key])) return overrides[key];
    }

    return {};
  }

  function classifyFromText(section, mode) {
    const haystack = [
      section.id,
      section.dataset.sectionId,
      section.dataset.eomSectionId,
      section.dataset.eomSectionName,
      section.className
    ].join(" ").toLowerCase();

    if (mode === "static") return "static";
    if (haystack.includes("footer")) return "footer-static";
    if (haystack.includes("hero")) return "critical";
    if (haystack.includes("matrix") || haystack.includes("product-grid") || haystack.includes("product_grid")) return "card-grid";
    if (haystack.includes("quiz") || haystack.includes("modal")) return "interactive";
    return "visual";
  }

  function getSectionType(section, mode, override) {
    return override.type || section.dataset.eomManagerType || section.dataset.eomVirtualType || classifyFromText(section, mode);
  }

  function normalizeMatcher(value) {
    return String(value || "").trim().toLowerCase();
  }

  function getSectionHaystack(section, index) {
    return [
      section.dataset.sectionId,
      section.id,
      section.dataset.eomSectionId,
      section.dataset.eomSectionName,
      section.dataset.eomHomeChunkSource,
      section.dataset.shopifyEditorSection,
      section.className,
      `managed-section-${index}`
    ].filter(Boolean).join(" ").toLowerCase();
  }

  function resolveHomeChunk(section, index, type, override = {}) {
    const stampedByManifest = section.dataset.eomHomeChunkSource === "manifest";
    const manualId = (stampedByManifest ? "" : section.dataset.eomHomeChunkId) || override.homeChunkId || override.chunkId;
    const chunks = Array.isArray(config.homeChunks) ? config.homeChunks : [];

    if (manualId) {
      const found = chunks.find((chunk) => chunk.id === manualId);

      return {
        id: manualId,
        label: found?.label || override.homeChunkLabel || manualId,
        order: toNumber(found?.order ?? override.homeChunkOrder, index * 10),
        strategy: found?.strategy || override.homeChunkStrategy || "manual",
        role: found?.role || override.homeChunkRole || type,
        fetchable: Boolean(found?.fetchable ?? override.homeChunkFetchable ?? true),
        source: "manual"
      };
    }

    const haystack = getSectionHaystack(section, index);
    const found = chunks.find((chunk) => {
      const matchers = Array.isArray(chunk.match) ? chunk.match : [];
      return matchers.some((matcher) => {
        const normalized = normalizeMatcher(matcher);
        return normalized && haystack.includes(normalized);
      });
    });

    if (found) {
      return {
        id: found.id,
        label: found.label || found.id,
        order: toNumber(found.order, index * 10),
        strategy: found.strategy || "future-fetch",
        role: found.role || type,
        fetchable: Boolean(found.fetchable),
        source: "manifest"
      };
    }

    return {
      id: "",
      label: "",
      order: null,
      strategy: "",
      role: "",
      fetchable: false,
      source: "unmatched"
    };
  }

  function stampHomeChunkAttributes(section, chunk) {
    if (!section || !chunk?.id) return;

    section.dataset.eomHomeChunkId = chunk.id;
    section.dataset.eomHomeChunkLabel = chunk.label || chunk.id;
    section.dataset.eomHomeChunkOrder = String(chunk.order ?? "");
    section.dataset.eomHomeChunkStrategy = chunk.strategy || "";
    section.dataset.eomHomeChunkRole = chunk.role || "";
    section.dataset.eomHomeChunkFetchable = chunk.fetchable ? "true" : "false";
    section.dataset.eomHomeChunkSource = chunk.source || "manifest";
  }

  function shouldShowChunkPlaceholderPreview() {
    if (config.chunkPlaceholderPreview === true) return true;
    return config.chunkPlaceholderPreviewInEditor !== false && Boolean(window.Shopify?.designMode);
  }

  function ensureChunkPreviewStyle() {
    if (document.getElementById(PREVIEW_STYLE_ID)) return;

    const style = document.createElement("style");
    style.id = PREVIEW_STYLE_ID;
    style.textContent = `
      #${PREVIEW_LAYER_ID} {
        position: absolute;
        inset: 0 auto auto 0;
        width: 100%;
        pointer-events: none;
        z-index: 2147483000;
      }

      #${PREVIEW_LAYER_ID} [data-eom-home-chunk-placeholder] {
        position: absolute;
        left: max(10px, env(safe-area-inset-left));
        right: max(10px, env(safe-area-inset-right));
        min-height: 160px;
        border: 2px dashed rgba(80, 190, 60, 0.72);
        border-radius: 10px;
        background:
          linear-gradient(105deg, transparent 34%, rgba(255, 255, 255, 0.72) 46%, transparent 58%),
          repeating-linear-gradient(135deg, rgba(80, 190, 60, 0.09) 0 14px, rgba(23, 35, 21, 0.04) 14px 28px);
        background-size: 260% 100%, auto;
        box-shadow: inset 0 0 0 1px rgba(16, 38, 20, 0.08), 0 20px 50px rgba(16, 38, 20, 0.12);
        animation: eomHomeChunkPlaceholderShine 2.4s ease-in-out infinite;
      }

      #${PREVIEW_LAYER_ID} [data-eom-home-chunk-placeholder]::before {
        content: attr(data-eom-home-chunk-label) " / " attr(data-eom-home-chunk-height) "px";
        position: sticky;
        top: 12px;
        display: inline-flex;
        margin: 12px;
        padding: 8px 10px;
        border-radius: 999px;
        background: rgba(16, 38, 20, 0.92);
        color: #ffffff;
        font: 800 12px/1.2 ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        box-shadow: 0 8px 22px rgba(16, 38, 20, 0.18);
      }

      @keyframes eomHomeChunkPlaceholderShine {
        0% { background-position: 150% 0, 0 0; }
        48%, 100% { background-position: -90% 0, 0 0; }
      }

      @media (prefers-reduced-motion: reduce) {
        #${PREVIEW_LAYER_ID} [data-eom-home-chunk-placeholder] {
          animation: none !important;
        }
      }
    `;
    document.head.appendChild(style);
  }

  function removeChunkPlaceholderPreview() {
    document.getElementById(PREVIEW_LAYER_ID)?.remove();
  }

  function getChunkPlaceholderModels(sections) {
    const configuredChunks = Array.isArray(config.homeChunks) ? config.homeChunks : [];
    const scrollY = window.scrollY || 0;

    return configuredChunks.map((chunk) => {
      const chunkSections = sections.filter((section) => section.homeChunkId === chunk.id);
      const tops = chunkSections.map((section) => section.top + scrollY);
      const bottoms = chunkSections.map((section) => section.bottom + scrollY);
      const top = tops.length ? Math.min(...tops) : 0;
      const bottom = bottoms.length ? Math.max(...bottoms) : 0;
      const measuredHeight = Math.max(Math.round(bottom - top), 0);
      const summedHeight = chunkSections.reduce((sum, section) => sum + Math.max(section.height, 0), 0);
      const reservedHeight = Math.max(measuredHeight, summedHeight, 0);

      return {
        id: chunk.id,
        label: chunk.label || chunk.id,
        order: toNumber(chunk.order, 0),
        strategy: chunk.strategy || "",
        role: chunk.role || "",
        fetchable: Boolean(chunk.fetchable),
        sectionCount: chunkSections.length,
        top: Math.round(top),
        height: reservedHeight,
        measuredHeight,
        summedHeight,
        stable: chunkSections.length > 0 && reservedHeight > 0,
        previewVisible: shouldShowChunkPlaceholderPreview()
      };
    });
  }

  function renderChunkPlaceholderPreview(models) {
    if (!shouldShowChunkPlaceholderPreview()) {
      removeChunkPlaceholderPreview();
      return;
    }

    ensureChunkPreviewStyle();

    let layer = document.getElementById(PREVIEW_LAYER_ID);
    if (!layer) {
      layer = document.createElement("div");
      layer.id = PREVIEW_LAYER_ID;
      layer.setAttribute("aria-hidden", "true");
      document.body.appendChild(layer);
    }

    layer.replaceChildren();

    models
      .filter((model) => model.stable)
      .forEach((model) => {
        const placeholder = document.createElement("div");
        placeholder.dataset.eomHomeChunkPlaceholder = model.id;
        placeholder.dataset.eomHomeChunkLabel = model.label;
        placeholder.dataset.eomHomeChunkHeight = String(model.height);
        placeholder.style.top = `${model.top}px`;
        placeholder.style.height = `${model.height}px`;
        placeholder.style.minHeight = `${model.height}px`;
        layer.appendChild(placeholder);
      });
  }

  function getFetchPocConfig() {
    const pocConfig = isPlainObject(config.homeChunkFetchPoc) ? config.homeChunkFetchPoc : {};

    return {
      enabled: pocConfig.enabled !== false,
      autoStart: pocConfig.autoStart !== false,
      targetChunkId: pocConfig.targetChunkId || "home-close",
      url: pocConfig.url || "",
      mountHidden: pocConfig.mountHidden !== false
    };
  }

  function getChunkFetchManagerConfig() {
    const managerConfig = isPlainObject(config.homeChunkFetchManager) ? config.homeChunkFetchManager : {};
    const fallback = DEFAULT_CONFIG.homeChunkFetchManager;

    return {
      enabled: managerConfig.enabled !== false,
      autoStart: managerConfig.autoStart !== false,
      mobileOnly: managerConfig.mobileOnly !== false,
      url: managerConfig.url || "",
      mountHidden: managerConfig.mountHidden !== false,
      fetchAheadViewports: toNumber(managerConfig.fetchAheadViewports, fallback.fetchAheadViewports),
      hydrateAheadViewports: toNumber(managerConfig.hydrateAheadViewports, fallback.hydrateAheadViewports),
      offloadViewports: toNumber(managerConfig.offloadViewports, fallback.offloadViewports),
      restoreBehindViewports: toNumber(managerConfig.restoreBehindViewports, fallback.restoreBehindViewports),
      compactAheadViewports: toNumber(managerConfig.compactAheadViewports, fallback.compactAheadViewports),
      maxConcurrent: Math.max(1, toNumber(managerConfig.maxConcurrent, fallback.maxConcurrent)),
      cache: managerConfig.cache !== false,
      skipChunkIds: Array.isArray(managerConfig.skipChunkIds) ? [...managerConfig.skipChunkIds] : [...fallback.skipChunkIds]
    };
  }

  function isChunkFetchManagerViewport(settings = getChunkFetchManagerConfig()) {
    if (!settings.enabled) return false;
    if (!settings.mobileOnly) return true;

    const mobileSettings = getMobileInitialReductionConfig();
    return isMobileInitialReductionViewport(mobileSettings);
  }

  function getChunkHeightReservationConfig() {
    const settings = isPlainObject(config.homeChunkHeightReservation)
      ? config.homeChunkHeightReservation
      : DEFAULT_CONFIG.homeChunkHeightReservation;

    return {
      enabled: settings.enabled !== false,
      mobileOnly: settings.mobileOnly !== false,
      minHeight: toNumber(settings.minHeight, DEFAULT_CONFIG.homeChunkHeightReservation.minHeight),
      correctionTolerance: toNumber(settings.correctionTolerance, DEFAULT_CONFIG.homeChunkHeightReservation.correctionTolerance),
      compensateFarAbove: settings.compensateFarAbove !== false,
      roleEstimates: {
        ...(DEFAULT_CONFIG.homeChunkHeightReservation.roleEstimates || {}),
        ...(settings.roleEstimates || {})
      }
    };
  }

  function isChunkHeightReservationViewport(settings = getChunkHeightReservationConfig()) {
    if (!settings.enabled) return false;
    if (!settings.mobileOnly) return true;

    const mobileSettings = getMobileInitialReductionConfig();
    return isMobileInitialReductionViewport(mobileSettings);
  }

  function getFetchPocUrl(pocConfig) {
    if (pocConfig.url) return pocConfig.url;

    const url = new URL(window.location.href);
    url.hash = "";
    url.searchParams.set("eom_chunk_poc", pocConfig.targetChunkId);
    return url.toString();
  }

  function getChunkFetchUrl(chunkId, settings = getChunkFetchManagerConfig()) {
    if (settings.url) {
      const url = new URL(settings.url, window.location.href);
      url.hash = "";
      url.searchParams.set("eom_home_chunk", chunkId);
      return url.toString();
    }

    const url = new URL(window.location.href);
    url.hash = "";
    url.searchParams.set("eom_home_chunk", chunkId);
    return url.toString();
  }

  function ensureFetchPocMount() {
    let mount = document.getElementById(FETCH_POC_MOUNT_ID);

    if (!mount) {
      mount = document.createElement("div");
      mount.id = FETCH_POC_MOUNT_ID;
      mount.setAttribute("aria-hidden", "true");
      mount.setAttribute("data-eom-home-chunk-fetch-poc-mount", "");
      mount.hidden = true;
      mount.style.display = "none";
      document.body.appendChild(mount);
    }

    return mount;
  }

  function sanitizeFetchedChunk(fragment) {
    fragment.querySelectorAll("[id]").forEach((node) => {
      const originalId = node.getAttribute("id");
      if (!originalId) return;
      node.dataset.eomChunkOriginalId = originalId;
      node.setAttribute("id", `eom-chunk-${originalId}`);
    });

    fragment.querySelectorAll("img, source").forEach((media) => {
      if (media.dataset.eomChunkOriginalSrc === undefined) media.dataset.eomChunkOriginalSrc = media.getAttribute("src") || "";
      if (media.dataset.eomChunkOriginalSrcset === undefined) media.dataset.eomChunkOriginalSrcset = media.getAttribute("srcset") || "";
      if (media.dataset.eomChunkOriginalSizes === undefined) media.dataset.eomChunkOriginalSizes = media.getAttribute("sizes") || "";

      media.removeAttribute("src");
      media.removeAttribute("srcset");
      media.removeAttribute("sizes");
      media.setAttribute("loading", "lazy");
    });

    fragment.querySelectorAll("script").forEach((script) => {
      const type = (script.getAttribute("type") || "").trim().toLowerCase();
      if (!type || type === "text/javascript" || type === "application/javascript" || type === "module") {
        script.remove();
      }
    });
  }

  function collectFetchedChunkSections(doc, targetChunkId) {
    const roots = Array.from(doc.querySelectorAll(config.selector));

    return roots.filter((root, index) => {
      const mode = root.dataset.eomVirtualMode || "unknown";
      const type = getSectionType(root, mode, {});
      const chunk = resolveHomeChunk(root, index, type, {});
      return chunk.id === targetChunkId;
    });
  }

  function getChunkRecord(chunkId) {
    if (!state.chunkFetchManager.chunks[chunkId]) {
      state.chunkFetchManager.chunks[chunkId] = {
        id: chunkId,
        state: "pending",
        url: "",
        requested: false,
        fetching: false,
        fetched: false,
        mounted: false,
        offloaded: false,
        restored: false,
        error: "",
        requestedAt: "",
        fetchedAt: "",
        mountedAt: "",
        offloadedAt: "",
        restoredAt: "",
        requestCount: 0,
        cacheHits: 0,
        restoredCount: 0,
        fetchedBytes: 0,
        mountedSections: 0,
        mountedNodes: 0
      };
    }

    return state.chunkFetchManager.chunks[chunkId];
  }

  function setChunkRecord(chunkId, patch = {}) {
    const previous = getChunkRecord(chunkId);
    state.chunkFetchManager.chunks[chunkId] = {
      ...previous,
      ...patch,
      id: chunkId
    };

    return state.chunkFetchManager.chunks[chunkId];
  }

  function summarizeChunkFetchManager() {
    const records = Object.values(state.chunkFetchManager.chunks || {});
    const fetchedChunks = records.filter((record) => record.fetched).length;
    const repeatedNetworkRequests = Math.max(
      records.reduce((sum, record) => sum + Math.max((record.requestCount || 0) - 1, 0), 0),
      Math.max((state.chunkFetchManager.sourceNetworkRequests || 0) - 1, 0)
    );
    const avoidedFullPageRequests = Math.max(fetchedChunks - (state.chunkFetchManager.sourceNetworkRequests || 0), 0);
    const sourceNetworkBytes = state.chunkFetchManager.sourceNetworkBytes || 0;
    const extractedPayloadBytes = records.reduce((sum, record) => sum + (record.fetchedBytes || 0), 0);
    const estimatedLegacyBytes = sourceNetworkBytes * Math.max(fetchedChunks, 1);
    const payloadReductionEstimatePercent = estimatedLegacyBytes
      ? Math.max(0, Math.round((1 - (sourceNetworkBytes / estimatedLegacyBytes)) * 1000) / 10)
      : 0;

    return {
      requestedChunks: records.filter((record) => record.requested).length,
      fetchingChunks: records.filter((record) => record.fetching).length,
      fetchedChunks,
      mountedChunks: records.filter((record) => record.mounted).length,
      offloadedChunks: records.filter((record) => record.offloaded).length,
      restoredChunks: records.filter((record) => record.restored || record.restoredCount > 0).length,
      repeatedNetworkRequests,
      totalFetchedBytes: extractedPayloadBytes,
      sourceNetworkRequests: state.chunkFetchManager.sourceNetworkRequests || 0,
      sourceNetworkBytes,
      extractedPayloadBytes,
      avoidedFullPageRequests,
      payloadReductionEstimatePercent,
      totalMountedNodes: records.reduce((sum, record) => sum + (record.mountedNodes || 0), 0),
      errorChunks: records.filter((record) => record.state === "error" || record.error).length
    };
  }

  function getFetchableChunkIds(settings = getChunkFetchManagerConfig()) {
    const configuredChunks = Array.isArray(config.homeChunks) ? config.homeChunks : [];

    return configuredChunks
      .filter((chunk) => {
        return chunk.strategy !== "initial" &&
          Boolean(chunk.fetchable) &&
          !settings.skipChunkIds.includes(chunk.id);
      })
      .map((chunk) => chunk.id)
      .filter(Boolean);
  }

  function cacheExtractedChunkPayload(chunkId, chunkSections) {
    const fragment = document.createDocumentFragment();

    chunkSections.forEach((section) => {
      fragment.appendChild(document.importNode(section, true));
    });
    sanitizeFetchedChunk(fragment);

    const wrapper = document.createElement("div");
    wrapper.appendChild(fragment);
    const payload = wrapper.innerHTML;
    const payloadBytes = new Blob([payload]).size;

    chunkPayloadCache.set(chunkId, payload);

    setChunkRecord(chunkId, {
      state: "cached",
      fetching: false,
      fetched: true,
      mounted: false,
      offloaded: false,
      restored: false,
      error: "",
      fetchedAt: new Date().toISOString(),
      fetchedBytes: payloadBytes,
      mountedSections: chunkSections.length,
      mountedNodes: wrapper.querySelectorAll("*").length
    });

    return payloadBytes;
  }

  async function ensureChunkPayloadSource(settings = getChunkFetchManagerConfig()) {
    if (chunkPayloadSourcePromise) return chunkPayloadSourcePromise;

    const chunkIds = getFetchableChunkIds(settings);
    const allCached = chunkIds.length > 0 && chunkIds.every((chunkId) => chunkPayloadCache.has(chunkId));

    if (allCached) {
      state.chunkFetchManager.payloadSourceState = "cached";
      return getSnapshot();
    }

    const url = getChunkFetchUrl("all-mobile-deferred", settings);

    state.chunkFetchManager.payloadSourceState = "fetching";
    state.chunkFetchManager.payloadSourceError = "";
    state.chunkFetchManager.sourceNetworkRequests = (state.chunkFetchManager.sourceNetworkRequests || 0) + 1;
    state.chunkFetchManager.lastActionAt = new Date().toISOString();

    chunkPayloadSourcePromise = fetch(url, {
      credentials: "same-origin",
      cache: settings.cache ? "force-cache" : "no-store"
    })
      .then(async (response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const html = await response.text();
        const sourceBytes = new Blob([html]).size;
        const doc = new DOMParser().parseFromString(html, "text/html");
        let extractedBytes = 0;
        let extractedChunks = 0;

        chunkIds.forEach((chunkId) => {
          const chunkSections = collectFetchedChunkSections(doc, chunkId);
          if (!chunkSections.length) return;

          extractedBytes += cacheExtractedChunkPayload(chunkId, chunkSections);
          extractedChunks += 1;

          logLifecycle("chunk-payload-extracted", { id: chunkId, name: chunkId, state: "cached" }, {
            targetChunkId: chunkId,
            mountedSections: chunkSections.length
          });
        });

        if (!extractedChunks) {
          throw new Error("No mobile-deferred chunk payloads were extracted");
        }

        state.chunkFetchManager.sourceNetworkBytes = sourceBytes;
        state.chunkFetchManager.extractedPayloadBytes = extractedBytes;
        state.chunkFetchManager.payloadSourceState = "cached";
        state.chunkFetchManager.payloadSourceError = "";

        Object.assign(state.chunkFetchManager, summarizeChunkFetchManager());
        return getSnapshot();
      })
      .catch((error) => {
        state.chunkFetchManager.payloadSourceState = "error";
        state.chunkFetchManager.payloadSourceError = error?.message || "payload source fetch failed";
        throw error;
      })
      .finally(() => {
        chunkPayloadSourcePromise = null;
      });

    return chunkPayloadSourcePromise;
  }

  function setFetchPocState(nextState = {}) {
    state.chunkFetchPoc = {
      ...state.chunkFetchPoc,
      ...nextState
    };
  }

  async function startHomeChunkFetchPoc(options = {}) {
    const pocConfig = {
      ...getFetchPocConfig(),
      ...options
    };

    if (!pocConfig.enabled) {
      setFetchPocState({
        state: "pending",
        targetChunkId: pocConfig.targetChunkId,
        url: pocConfig.url || "",
        mounted: false,
        error: "disabled"
      });
      refresh();
      return getSnapshot();
    }

    if (fetchPocPromise) return fetchPocPromise;

    const url = getFetchPocUrl(pocConfig);
    const targetChunkId = pocConfig.targetChunkId;

    setFetchPocState({
      state: state.chunkFetchPoc.mounted ? "restored" : "fetching",
      targetChunkId,
      url,
      mounted: false,
      error: "",
      requestedAt: new Date().toISOString()
    });

    refresh();

    fetchPocPromise = fetch(url, {
      credentials: "same-origin",
      cache: "no-store"
    })
      .then(async (response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, "text/html");
        const chunkSections = collectFetchedChunkSections(doc, targetChunkId);

        if (!chunkSections.length) {
          throw new Error(`No sections matched ${targetChunkId}`);
        }

        const fragment = document.createDocumentFragment();
        chunkSections.forEach((section) => {
          fragment.appendChild(document.importNode(section, true));
        });
        sanitizeFetchedChunk(fragment);

        const mount = ensureFetchPocMount();
        mount.replaceChildren(fragment);
        mount.hidden = pocConfig.mountHidden;
        mount.style.display = pocConfig.mountHidden ? "none" : "block";
        mount.dataset.eomHomeChunkFetchPocState = "mounted";
        mount.dataset.eomHomeChunkFetchPocTarget = targetChunkId;

        setFetchPocState({
          state: "mounted",
          targetChunkId,
          url,
          mounted: true,
          error: "",
          mountedAt: new Date().toISOString(),
          restoredAt: state.chunkFetchPoc.mounted ? new Date().toISOString() : state.chunkFetchPoc.restoredAt,
          restoredCount: state.chunkFetchPoc.mounted ? state.chunkFetchPoc.restoredCount + 1 : state.chunkFetchPoc.restoredCount,
          fetchedBytes: new Blob([html]).size,
          mountedSections: chunkSections.length,
          mountedNodes: mount.querySelectorAll("*").length
        });

        logLifecycle("chunk-fetch-poc-mounted", { id: targetChunkId, name: targetChunkId, state: "mounted" }, {
          targetChunkId,
          mountedSections: chunkSections.length
        });

        refresh();
        return getSnapshot();
      })
      .catch((error) => {
        setFetchPocState({
          state: "error",
          targetChunkId,
          url,
          mounted: false,
          error: error?.message || "fetch failed"
        });

        logLifecycle("chunk-fetch-poc-error", { id: targetChunkId, name: targetChunkId, state: "error" }, {
          targetChunkId,
          error: state.chunkFetchPoc.error
        });

        refresh();
        return getSnapshot();
      })
      .finally(() => {
        fetchPocPromise = null;
      });

    return fetchPocPromise;
  }

  async function fetchHomeChunk(chunkId, options = {}) {
    const settings = {
      ...getChunkFetchManagerConfig(),
      ...options
    };
    const record = getChunkRecord(chunkId);

    if (!settings.enabled || !chunkId || settings.skipChunkIds.includes(chunkId)) {
      setChunkRecord(chunkId, {
        state: "pending",
        error: settings.enabled ? "skipped" : "disabled"
      });
      return getSnapshot();
    }

    if (settings.cache && record.fetched) {
      setChunkRecord(chunkId, {
        state: record.offloaded ? "restored" : record.state,
        requested: true,
        restored: record.offloaded || record.restored,
        restoredAt: record.offloaded ? new Date().toISOString() : record.restoredAt,
        restoredCount: record.offloaded ? (record.restoredCount || 0) + 1 : record.restoredCount,
        offloaded: false,
        cacheHits: (record.cacheHits || 0) + 1,
        error: ""
      });
      return getSnapshot();
    }

    if (chunkFetchPromises.has(chunkId)) return chunkFetchPromises.get(chunkId);

    const activeFetches = Array.from(chunkFetchPromises.values()).length;
    if (activeFetches >= settings.maxConcurrent) {
      setChunkRecord(chunkId, {
        state: "pending",
        error: "waiting for fetch slot"
      });
      return getSnapshot();
    }

    const url = getChunkFetchUrl(chunkId, settings);
    const requestCount = (record.requestCount || 0) + 1;

    setChunkRecord(chunkId, {
      state: "fetching",
      url,
      requested: true,
      fetching: true,
      fetched: false,
      mounted: false,
      offloaded: false,
      error: "",
      requestedAt: new Date().toISOString(),
      requestCount
    });

    state.chunkFetchManager.state = "fetching";
    state.chunkFetchManager.lastActionAt = new Date().toISOString();

    const promise = ensureChunkPayloadSource(settings)
      .then(() => {
        const payload = chunkPayloadCache.get(chunkId) || "";
        const cachedRecord = getChunkRecord(chunkId);

        if (!payload) {
          throw new Error(`No extracted payload matched ${chunkId}`);
        }

        setChunkRecord(chunkId, {
          state: "cached",
          url,
          fetching: false,
          fetched: true,
          mounted: false,
          offloaded: false,
          restored: false,
          error: "",
          fetchedAt: cachedRecord.fetchedAt || new Date().toISOString(),
          fetchedBytes: cachedRecord.fetchedBytes || new Blob([payload]).size,
          mountedSections: cachedRecord.mountedSections || 0,
          mountedNodes: cachedRecord.mountedNodes || 0
        });

        logLifecycle("chunk-fetch-cached", { id: chunkId, name: chunkId, state: "cached" }, {
          targetChunkId: chunkId,
          mountedSections: cachedRecord.mountedSections || 0
        });

        refresh();
        return getSnapshot();
      })
      .catch((error) => {
        setChunkRecord(chunkId, {
          state: "error",
          url,
          fetching: false,
          fetched: false,
          mounted: false,
          error: error?.message || "fetch failed"
        });

        logLifecycle("chunk-fetch-error", { id: chunkId, name: chunkId, state: "error" }, {
          targetChunkId: chunkId,
          error: error?.message || "fetch failed"
        });

        refresh();
        return getSnapshot();
      })
      .finally(() => {
        chunkFetchPromises.delete(chunkId);
      });

    chunkFetchPromises.set(chunkId, promise);
    refresh();
    return promise;
  }

  function buildChunkRuntimeModels(sections, settings) {
    const configuredChunks = Array.isArray(config.homeChunks) ? config.homeChunks : [];
    const viewportHeight = isChunkFetchManagerViewport(settings)
      ? getStableMobileViewportHeight()
      : (window.innerHeight || 1);

    return configuredChunks.map((chunk) => {
      const chunkSections = sections.filter((section) => section.homeChunkId === chunk.id);
      const top = chunkSections.length ? Math.min(...chunkSections.map((section) => section.top)) : Infinity;
      const bottom = chunkSections.length ? Math.max(...chunkSections.map((section) => section.bottom)) : -Infinity;
      const order = toNumber(chunk.order, 0);
      const initial = chunk.strategy === "initial";
      const mobileDeferred = !initial && Boolean(chunk.fetchable) && !settings.skipChunkIds.includes(chunk.id);
      const behindLimit = viewportHeight * settings.restoreBehindViewports;
      const fetchNear = mobileDeferred && top < viewportHeight * settings.fetchAheadViewports && bottom > -behindLimit;
      const hydrateNear = mobileDeferred && top < viewportHeight * settings.hydrateAheadViewports && bottom > -behindLimit;
      const visiblePathProtected = mobileDeferred &&
        MOBILE_VISIBLE_PATH_PROTECTED_CHUNK_IDS.has(chunk.id) &&
        top < viewportHeight * 3 &&
        bottom > -viewportHeight * settings.offloadViewports;
      const shellSectionCount = chunkSections.filter((section) => {
        return section.state === "shell" || isSectionHeightLocked(section.root);
      }).length;
      const farAway = mobileDeferred && (
        bottom < -viewportHeight * settings.offloadViewports ||
        top > viewportHeight * (settings.fetchAheadViewports + settings.offloadViewports)
      );
      const safeToCompact = mobileDeferred && (
        bottom < -viewportHeight * settings.offloadViewports ||
        top > viewportHeight * settings.compactAheadViewports
      );

      return {
        id: chunk.id,
        label: chunk.label || chunk.id,
        order,
        role: chunk.role || "",
        fetchable: Boolean(chunk.fetchable),
        initial,
        mobileDeferred,
        sectionCount: chunkSections.length,
        sections: chunkSections,
        top,
        bottom,
        fetchNear,
        hydrateNear,
        visiblePathProtected,
        shellSectionCount,
        farAway,
        safeToCompact,
        stableViewportHeight: viewportHeight
      };
    });
  }

  function getChunkHeightRecord(chunkId) {
    if (!state.chunkHeightReservations.chunks[chunkId]) {
      state.chunkHeightReservations.chunks[chunkId] = {
        id: chunkId,
        source: "estimated",
        measuredHeight: 0,
        reservedHeight: 0,
        estimatedHeight: 0,
        sectionHeights: {},
        stable: false,
        lastMeasuredAt: "",
        lastReservedAt: "",
        adjustmentCount: 0,
        scrollCompensations: 0,
        scrollCompensationPixels: 0,
        activeReservationReleased: false,
        appliedToLayout: false
      };
    }

    return state.chunkHeightReservations.chunks[chunkId];
  }

  function getMeasuredChunkHeight(model) {
    return model.sections.reduce((sum, section) => {
      return sum + Math.max(toNumber(section.height, 0), 0);
    }, 0);
  }

  function getEstimatedChunkHeight(model, settings) {
    const byRole = settings.roleEstimates?.[model.role] ||
      settings.roleEstimates?.[model.id] ||
      settings.roleEstimates?.visual ||
      0;
    const sectionEstimate = Math.max(model.sectionCount, 1) * settings.minHeight;

    return Math.max(toNumber(byRole, 0), sectionEstimate, settings.minHeight);
  }

  function getStableMobileViewportHeight() {
    const current = window.visualViewport?.height || window.innerHeight || document.documentElement.clientHeight || 1;
    const previous = toNumber(state.stableMobileViewportHeight, 0);
    const next = Math.max(previous, Math.round(current));

    state.stableMobileViewportHeight = next;
    return next || 1;
  }

  function isSectionHeightLocked(root) {
    return Boolean(root?.classList?.contains("is-eom-height-locked") || root?.classList?.contains("is-height-locked"));
  }

  function releaseSectionHeightLock(section) {
    const root = section?.root;
    if (!root) return false;

    const hadLock = isSectionHeightLocked(root);
    root.classList.remove("is-eom-height-locked", "is-height-locked");
    if (hadLock) {
      root.style.removeProperty("--eom-locked-height");
      root.style.removeProperty("min-height");
    }
    return hadLock;
  }

  function isMobileVisiblePathProtectedSection(section, viewportHeight = getStableMobileViewportHeight()) {
    if (!section?.homeChunkId || !isMobileInitialReductionViewport()) return false;
    if (!MOBILE_VISIBLE_PATH_PROTECTED_CHUNK_IDS.has(section.homeChunkId)) return false;

    return section.top < viewportHeight * 3 && section.bottom > -viewportHeight * 4;
  }

  function isMobileStableVisualRoot(root) {
    if (!root || !isMobileInitialReductionViewport()) return false;
    if (MOBILE_STABLE_VISUAL_SECTION_KINDS.has(root.dataset.eomSectionKind)) return true;
    return false;
  }

  function mountVisualSlotsPermanently(root) {
    let mounted = 0;

    root.querySelectorAll("[data-eom-visual-slot]").forEach((slot) => {
      const template = slot.querySelector("[data-eom-visual-template]");
      const mount = slot.querySelector("[data-eom-visual-mount]");
      if (!template || !mount || mount.childElementCount) return;

      mount.replaceChildren(template.content.cloneNode(true));
      mounted += 1;
    });

    return mounted;
  }

  function isRepeatDeliveryRoot(root) {
    if (!root) return false;
    if (root.dataset.eomSectionKind === "featured-repeat-delivery") return true;

    const className = String(root.className || "");
    return className.includes("repeat-sub-section__") || className.includes("featured_subscription_plan");
  }

  function enforceCleanVisualOnlyRoots(roots) {
    const result = {
      cleanVisualOnlySections: 0,
      heightLocksReleased: 0
    };

    if (!isMobileInitialReductionViewport()) return result;

    roots.forEach((root) => {
      if (!isRepeatDeliveryRoot(root)) return;

      root.dataset.eomVisualOnly = "true";
      root.dataset.eomNoHeightLock = "true";

      if (root.dataset.eomMobileStableVisual === "true") {
        root.dataset.eomEnableVirtualization = root.dataset.eomOriginalEnableVirtualization || root.dataset.eomEnableVirtualization || "true";
        delete root.dataset.eomMobileStableVisual;
        delete root.dataset.eomOriginalEnableVirtualization;
      }

      if (isSectionHeightLocked(root)) {
        root.classList.remove("is-eom-height-locked", "is-height-locked");
        root.style.removeProperty("--eom-locked-height");
        result.heightLocksReleased += 1;
      }

      if (root.dataset.eomHomeChunkReservedByManager === "true") {
        root.style.removeProperty("--eom-home-chunk-reserved-height");
        root.style.removeProperty("min-height");
        root.removeAttribute("data-eom-home-chunk-reserved-height");
        delete root.dataset.eomHomeChunkReservedByManager;
        result.heightLocksReleased += 1;
      }

      result.cleanVisualOnlySections += 1;
    });

    return result;
  }

  function stabilizeMobileVisualRoots(roots) {
    const result = {
      stabilizedSections: 0,
      mountedSlots: 0,
      heightLocksReleased: 0
    };

    roots.forEach((root) => {
      if (!root) return;

      if (!isMobileInitialReductionViewport() || !isMobileStableVisualRoot(root)) {
        if (root.dataset.eomMobileStableVisual !== "true") return;
        root.dataset.eomEnableVirtualization = root.dataset.eomOriginalEnableVirtualization || "true";
        delete root.dataset.eomMobileStableVisual;
        delete root.dataset.eomOriginalEnableVirtualization;
        return;
      }

      if (!root.dataset.eomOriginalEnableVirtualization) {
        root.dataset.eomOriginalEnableVirtualization = root.dataset.eomEnableVirtualization || "true";
      }

      root.dataset.eomMobileStableVisual = "true";
      root.dataset.eomEnableVirtualization = "false";
      root.dataset.eomVirtualState = "active";
      result.mountedSlots += mountVisualSlotsPermanently(root);

      if (isSectionHeightLocked(root)) {
        root.classList.remove("is-eom-height-locked", "is-height-locked");
        root.style.removeProperty("--eom-locked-height");
        result.heightLocksReleased += 1;
      }

      if (root.dataset.eomHomeChunkReservedByManager === "true") {
        root.style.removeProperty("--eom-home-chunk-reserved-height");
        root.style.removeProperty("min-height");
        root.removeAttribute("data-eom-home-chunk-reserved-height");
        delete root.dataset.eomHomeChunkReservedByManager;
        result.heightLocksReleased += 1;
      }

      result.stabilizedSections += 1;
    });

    return result;
  }

  function shouldReleaseActiveChunkReservation(model) {
    if (model.mobileDeferred && !model.farAway) return true;
    if (model.mobileDeferred && model.hydrateNear) return true;

    return model.sections.some((section) => {
      const nearBand = ["in-view", "ahead", "above"].includes(section.distanceBand);
      const activeState = section.state === "active" || section.state === "hydrating";

      return nearBand && (activeState || section.nearViewport === true);
    });
  }

  function shouldSkipChunkHeightReservation(model) {
    if (!model?.id) return false;
    if (HEIGHT_RESERVATION_EXCLUDED_CHUNK_IDS.has(model.id)) return true;

    return model.sections.length > 0 && model.sections.every((section) => {
      return section.enabled === false || section.mode === "static" || section.type === "footer-static";
    });
  }

  function distributeReservedHeight(model, record) {
    record.activeReservationExpected = shouldReleaseActiveChunkReservation(model);

    if (record.activeReservationExpected) {
      clearReservedHeight(model);
      record.activeReservationReleased = true;
      record.appliedToLayout = false;
      return;
    }

    const measuredEntries = Object.entries(record.sectionHeights || {}).filter(([, height]) => height > 0);
    const measuredTotal = measuredEntries.reduce((sum, [, height]) => sum + height, 0);
    const fallback = Math.max(Math.round((record.reservedHeight || 0) / Math.max(model.sections.length, 1)), 0);

    model.sections.forEach((section) => {
      const measured = record.sectionHeights?.[section.id] || 0;
      const proportion = measuredTotal > 0 ? measured / measuredTotal : 0;
      const reserved = proportion > 0
        ? Math.max(Math.round(record.reservedHeight * proportion), settingsSafeMinHeight())
        : fallback;

      if (!section.root || reserved <= 0) return;

      section.root.dataset.eomHomeChunkReservedHeight = String(reserved);
      section.root.dataset.eomHomeChunkReservedByManager = "true";
      section.root.style.setProperty("--eom-home-chunk-reserved-height", `${reserved}px`);
      section.root.style.minHeight = `${reserved}px`;
    });

    record.activeReservationReleased = false;
    record.appliedToLayout = true;
  }

  function settingsSafeMinHeight() {
    return Math.max(120, toNumber(config.homeChunkHeightReservation?.minHeight, DEFAULT_CONFIG.homeChunkHeightReservation.minHeight));
  }

  function clearReservedHeight(model) {
    model.sections.forEach((section) => {
      if (!section.root) return;
      section.root.style.removeProperty("--eom-home-chunk-reserved-height");
      section.root.removeAttribute("data-eom-home-chunk-reserved-height");

      if (section.root.dataset.eomHomeChunkReservedByManager === "true") {
        section.root.style.minHeight = "";
        delete section.root.dataset.eomHomeChunkReservedByManager;
      }
    });
  }

  function evaluateChunkHeightReservations(sections) {
    const settings = getChunkHeightReservationConfig();
    const viewportActive = isChunkHeightReservationViewport(settings);
    const result = {
      enabled: settings.enabled,
      viewportActive,
      measuredChunks: 0,
      estimatedChunks: 0,
      stableChunks: 0,
      unstableChunks: 0,
      totalReservedHeight: 0,
      totalMeasuredHeight: 0,
      maxHeightDelta: 0,
      scrollCompensations: 0,
      scrollCompensationPixels: 0,
      activeReservationExpectedChunks: 0,
      activeReservationReleasedChunks: 0,
      appliedReservationChunks: 0,
      cleanContractSkippedChunks: 0,
      activeReservationExpectedChunkIds: [],
      activeReservationReleasedChunkIds: [],
      appliedReservationChunkIds: [],
      cleanContractSkippedChunkIds: []
    };

    state.chunkHeightReservations.enabled = settings.enabled;
    state.chunkHeightReservations.viewportActive = viewportActive;

    const fetchSettings = getChunkFetchManagerConfig();
    const models = buildChunkRuntimeModels(sections, fetchSettings).filter((model) => {
      return model.mobileDeferred && model.sectionCount > 0;
    });

    if (!settings.enabled || !viewportActive || window.Shopify?.designMode) {
      models.forEach(clearReservedHeight);
      Object.assign(state.chunkHeightReservations, result);
      return result;
    }

    models.forEach((model) => {
      const record = getChunkHeightRecord(model.id);
      if (shouldSkipChunkHeightReservation(model)) {
        clearReservedHeight(model);
        record.activeReservationExpected = true;
        record.activeReservationReleased = true;
        record.appliedToLayout = false;
        record.skippedByCleanContract = true;
        result.cleanContractSkippedChunks += 1;
        result.cleanContractSkippedChunkIds.push(model.id);
        return;
      }

      const measuredHeight = getMeasuredChunkHeight(model);
      const canTrustMeasured = measuredHeight >= settings.minHeight;
      const estimatedHeight = getEstimatedChunkHeight(model, settings);
      const previousReserved = record.reservedHeight || 0;
      const sectionHeights = { ...(record.sectionHeights || {}) };

      model.sections.forEach((section) => {
        if (section.height >= 120) sectionHeights[section.id] = section.height;
      });

      const nextReserved = canTrustMeasured
        ? Math.max(measuredHeight, settings.minHeight)
        : Math.max(record.measuredHeight || 0, record.reservedHeight || 0, estimatedHeight);
      const source = canTrustMeasured ? "measured" : "estimated";
      const delta = nextReserved - previousReserved;
      const stable = previousReserved > 0 && Math.abs(delta) <= settings.correctionTolerance;

      record.source = source;
      record.measuredHeight = canTrustMeasured ? measuredHeight : record.measuredHeight || 0;
      record.estimatedHeight = estimatedHeight;
      record.reservedHeight = nextReserved;
      record.sectionHeights = sectionHeights;
      record.stable = stable || source === "measured";
      record.lastReservedAt = new Date().toISOString();

      if (canTrustMeasured) record.lastMeasuredAt = record.lastReservedAt;
      if (previousReserved && Math.abs(delta) > settings.correctionTolerance) {
        record.adjustmentCount += 1;
      }

      distributeReservedHeight(model, record);

      result.totalReservedHeight += record.reservedHeight;
      result.totalMeasuredHeight += record.measuredHeight || 0;
      result.maxHeightDelta = Math.max(result.maxHeightDelta, Math.abs((record.measuredHeight || record.estimatedHeight) - record.reservedHeight));
      if (record.activeReservationExpected) {
        result.activeReservationExpectedChunks += 1;
        result.activeReservationExpectedChunkIds.push(model.id);
      }
      if (record.activeReservationReleased) {
        result.activeReservationReleasedChunks += 1;
        result.activeReservationReleasedChunkIds.push(model.id);
      }
      if (record.appliedToLayout) {
        result.appliedReservationChunks += 1;
        result.appliedReservationChunkIds.push(model.id);
      }
      if (source === "measured") result.measuredChunks += 1;
      if (source === "estimated") result.estimatedChunks += 1;
      if (record.stable) result.stableChunks += 1;
      if (!record.stable) result.unstableChunks += 1;
    });

    Object.assign(state.chunkHeightReservations, result);
    return result;
  }

  function compactSectionChunkArtifacts(root) {
    if (!root || chunkArtifactCache.has(root)) return 0;

    const artifacts = Array.from(root.querySelectorAll(
      "[data-eom-virtual-template], [data-eom-visual-template], [data-eom-loader], [data-eom-visual-placeholder], script:not([type='application/ld+json']), style"
    )).filter((node) => {
      return !node.closest("[data-eom-home-chunk-cache-mount], [data-eom-home-chunk-fetch-poc-mount]");
    });

    if (!artifacts.length) return 0;

    const records = artifacts.map((node, index) => {
      const marker = document.createComment(`eom compacted chunk artifact ${index}`);
      const keepNode = node.matches("[data-eom-virtual-template], [data-eom-visual-template]");
      const scriptStyle = node.matches("script, style");
      const record = {
        marker,
        node: keepNode ? node : null,
        html: keepNode ? "" : node.outerHTML,
        scriptStyle
      };

      node.replaceWith(marker);
      return record;
    });
    const scriptStyleCount = records.filter((record) => record.scriptStyle).length;

    chunkArtifactCache.set(root, records);
    root.dataset.eomChunkArtifactsCompacted = "true";
    state.chunkArtifactCompaction.currentCompactedSections += 1;
    state.chunkArtifactCompaction.currentCompactedNodes += records.length;
    state.chunkArtifactCompaction.currentCompactedScriptStyleNodes += scriptStyleCount;
    state.chunkArtifactCompaction.totalCompactedSections += 1;
    state.chunkArtifactCompaction.totalCompactedNodes += records.length;
    state.chunkArtifactCompaction.totalCompactedScriptStyleNodes += scriptStyleCount;
    state.chunkArtifactCompaction.lastActionAt = new Date().toISOString();

    return records.length;
  }

  function restoreSectionChunkArtifacts(root) {
    const records = root ? chunkArtifactCache.get(root) : null;
    if (!records?.length) return 0;

    records.forEach(({ marker, node, html }) => {
      if (!marker.parentNode) return;
      if (node) {
        marker.replaceWith(node);
        return;
      }

      const template = document.createElement("template");
      template.innerHTML = html || "";
      marker.replaceWith(template.content.cloneNode(true));
    });

    chunkArtifactCache.delete(root);
    delete root.dataset.eomChunkArtifactsCompacted;
    const scriptStyleCount = records.filter((record) => record.scriptStyle).length;
    state.chunkArtifactCompaction.currentCompactedSections = Math.max(state.chunkArtifactCompaction.currentCompactedSections - 1, 0);
    state.chunkArtifactCompaction.currentCompactedNodes = Math.max(state.chunkArtifactCompaction.currentCompactedNodes - records.length, 0);
    state.chunkArtifactCompaction.currentCompactedScriptStyleNodes = Math.max(state.chunkArtifactCompaction.currentCompactedScriptStyleNodes - scriptStyleCount, 0);
    state.chunkArtifactCompaction.totalRestoredSections += 1;
    state.chunkArtifactCompaction.totalRestoredNodes += records.length;
    state.chunkArtifactCompaction.totalRestoredScriptStyleNodes += scriptStyleCount;
    state.chunkArtifactCompaction.lastActionAt = new Date().toISOString();

    return records.length;
  }

  function compactHomeChunkArtifacts(model) {
    if (!model?.sections?.length || window.Shopify?.designMode) return 0;

    return model.sections.reduce((sum, section) => {
      return sum + compactSectionChunkArtifacts(section.root);
    }, 0);
  }

  function restoreHomeChunkArtifacts(model) {
    if (!model?.sections?.length) return 0;

    return model.sections.reduce((sum, section) => {
      return sum + restoreSectionChunkArtifacts(section.root);
    }, 0);
  }

  function getNodeCount(nodes) {
    return nodes.reduce((sum, node) => {
      return sum + (node.nodeType === 1 ? 1 + node.querySelectorAll("*").length : 0);
    }, 0);
  }

  function getCompactText(root) {
    const clone = root.cloneNode(true);

    clone.querySelectorAll(
      "script, style, template, noscript, [data-eom-virtual-mount], [data-eom-visual-mount], [data-eom-loader], [data-eom-visual-placeholder], [data-matrix-card-slot]"
    ).forEach((node) => node.remove());

    return (clone.textContent || "").replace(/\s+/g, " ").trim().slice(0, 520);
  }

  function buildCompactSeoShell(root) {
    const shell = document.createElement("div");
    const heading = root.querySelector("h2, h3, h4");
    const links = Array.from(root.querySelectorAll("a[href]")).filter((link) => {
      const href = link.getAttribute("href") || "";
      const text = (link.textContent || "").replace(/\s+/g, " ").trim();
      return href && text && !href.startsWith("#");
    });
    const schemaScripts = Array.from(root.querySelectorAll('script[type="application/ld+json"]'));
    const text = getCompactText(root);

    shell.dataset.eomSeoCompactShell = "true";
    shell.className = "eom-seo-compact-shell";

    if (heading && (heading.textContent || "").trim()) {
      const compactHeading = document.createElement(heading.tagName.toLowerCase());
      compactHeading.textContent = (heading.textContent || "").replace(/\s+/g, " ").trim();
      shell.appendChild(compactHeading);
    }

    if (text) {
      const paragraph = document.createElement("p");
      paragraph.textContent = text;
      shell.appendChild(paragraph);
    }

    if (links.length) {
      const nav = document.createElement("nav");
      nav.setAttribute("aria-label", "Section links");

      links.slice(0, 12).forEach((link) => {
        const compactLink = document.createElement("a");
        compactLink.href = link.href;
        compactLink.textContent = (link.textContent || "").replace(/\s+/g, " ").trim();
        nav.appendChild(compactLink);
      });

      shell.appendChild(nav);
    }

    schemaScripts.forEach((script) => {
      const compactScript = document.createElement("script");
      compactScript.type = "application/ld+json";
      compactScript.textContent = script.textContent || "";
      shell.appendChild(compactScript);
    });

    return shell;
  }

  function isSeoShellCompactionCandidate(section) {
    if (!section?.root || window.Shopify?.designMode) return false;
    if (isMobileSeoShellCompactionProtected(section)) return false;
    if (section.homeChunkId === "home-product-discovery") return false;
    if (section.homeChunkId === "home-close") return false;
    if (section.type === "critical" || section.type === "card-grid" || section.type === "footer-static") return false;
    if (section.mode === "static" || section.enabled === false) return false;
    if (seoShellCache.has(section.root)) return false;
    return section.root.querySelector("h2, h3, h4, p, a[href], script[type='application/ld+json']");
  }

  function isMobileSeoShellCompactionProtected(section) {
    if (!section?.homeChunkId) return false;
    if (!isMobileInitialReductionViewport()) return false;

    return MOBILE_SEO_SHELL_PROTECTED_CHUNK_IDS.has(section.homeChunkId) ||
      section.homeChunkFetchable === true ||
      section.homeChunkStrategy !== "initial";
  }

  function compactSectionSeoShell(section) {
    if (!isSeoShellCompactionCandidate(section)) return 0;

    const root = section.root;
    const children = Array.from(root.childNodes);
    const originalNodes = getNodeCount(children);
    if (originalNodes < 16) return 0;

    const shell = buildCompactSeoShell(root);
    const compactNodes = getNodeCount([shell]);
    if (compactNodes <= 0 || compactNodes >= originalNodes) return 0;

    const placeholder = document.createComment("eom compacted seo shell");
    root.replaceChildren(placeholder, shell);
    root.dataset.eomSeoShellCompacted = "true";

    seoShellCache.set(root, {
      children,
      placeholder,
      shell,
      originalNodes,
      compactNodes
    });

    state.seoShellCompaction.currentCompactedSections += 1;
    state.seoShellCompaction.currentOriginalNodes += originalNodes;
    state.seoShellCompaction.currentCompactNodes += compactNodes;
    state.seoShellCompaction.totalCompactedSections += 1;
    state.seoShellCompaction.totalOriginalNodes += originalNodes;
    state.seoShellCompaction.totalCompactNodes += compactNodes;
    state.seoShellCompaction.lastActionAt = new Date().toISOString();

    return Math.max(originalNodes - compactNodes, 0);
  }

  function restoreSectionSeoShell(root) {
    const record = root ? seoShellCache.get(root) : null;
    if (!record) return 0;

    root.replaceChildren(...record.children);
    delete root.dataset.eomSeoShellCompacted;
    seoShellCache.delete(root);

    state.seoShellCompaction.currentCompactedSections = Math.max(state.seoShellCompaction.currentCompactedSections - 1, 0);
    state.seoShellCompaction.currentOriginalNodes = Math.max(state.seoShellCompaction.currentOriginalNodes - record.originalNodes, 0);
    state.seoShellCompaction.currentCompactNodes = Math.max(state.seoShellCompaction.currentCompactNodes - record.compactNodes, 0);
    state.seoShellCompaction.totalRestoredSections += 1;
    state.seoShellCompaction.totalRestoredNodes += record.originalNodes;
    state.seoShellCompaction.lastActionAt = new Date().toISOString();

    return record.originalNodes;
  }

  function compactHomeChunkSeoShells(model) {
    if (!model?.sections?.length || window.Shopify?.designMode) return 0;
    if (model.sections.some(isMobileSeoShellCompactionProtected)) {
      restoreHomeChunkSeoShells(model);
      return 0;
    }

    return model.sections.reduce((sum, section) => {
      return sum + compactSectionSeoShell(section);
    }, 0);
  }

  function restoreHomeChunkSeoShells(model) {
    if (!model?.sections?.length) return 0;

    return model.sections.reduce((sum, section) => {
      return sum + restoreSectionSeoShell(section.root);
    }, 0);
  }

  function restoreMobileProtectedSeoShells(models) {
    if (!isMobileInitialReductionViewport() || !Array.isArray(models)) return 0;

    return models.reduce((sum, model) => {
      if (!model?.sections?.some(isMobileSeoShellCompactionProtected)) return sum;
      return sum + restoreHomeChunkSeoShells(model);
    }, 0);
  }

  function isScrollAnchorRepairViewport() {
    const mobileSettings = getMobileInitialReductionConfig();
    return isMobileInitialReductionViewport(mobileSettings);
  }

  function shouldRepairChunkScrollAnchor(model) {
    if (!state.scrollAnchorRepair.enabled || window.Shopify?.designMode) return false;
    if (!model?.mobileDeferred || !model.sectionCount) return false;
    if (!isScrollAnchorRepairViewport()) return false;

    const viewportHeight = window.innerHeight || 1;
    return model.top < viewportHeight * 1.5;
  }

  function getScrollAnchorSnapshot(model) {
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 1;
    const excludedRoots = new Set((model?.sections || []).map((section) => section.root).filter(Boolean));
    const anchorSelector = [
      "[data-eom-virtual-section]",
      "[data-matrix-card]",
      "[data-product-card]",
      ".matrix-card",
      ".product-card",
      "section"
    ].join(",");
    const candidates = Array.from(document.querySelectorAll(anchorSelector)).filter((node) => {
      if (!node?.isConnected) return false;
      for (const root of excludedRoots) {
        if (node === root || root.contains(node)) return false;
      }

      const rect = node.getBoundingClientRect();
      const visible = rect.bottom > 8 && rect.top < viewportHeight - 8;

      return visible && rect.height >= 16 && rect.width >= 16;
    }).sort((a, b) => {
      const rectA = a.getBoundingClientRect();
      const rectB = b.getBoundingClientRect();
      const scoreA = Math.abs(Math.max(rectA.top, 0));
      const scoreB = Math.abs(Math.max(rectB.top, 0));
      return scoreA - scoreB;
    });

    for (const anchor of candidates) {
      const rect = anchor.getBoundingClientRect();
      return {
        node: anchor,
        top: rect.top,
        scrollY: window.scrollY || 0
      };
    }

    const fallback = document.elementFromPoint(
      Math.max(1, Math.round((window.innerWidth || 1) / 2)),
      Math.max(1, Math.round(viewportHeight * 0.35))
    )?.closest?.("main, #MainContent");

    if (fallback?.isConnected) {
      const rect = fallback.getBoundingClientRect();
      return {
        node: fallback,
        top: rect.top,
        scrollY: window.scrollY || 0
      };
    }

    return null;
  }

  function compensateScrollAnchor(anchor, model, action, phase) {
    if (!anchor?.node?.isConnected) return 0;

    const nextTop = anchor.node.getBoundingClientRect().top;
    const delta = nextTop - anchor.top;
    const maxSafeDelta = Math.min(640, (window.innerHeight || 1) * 0.85);

    if (Math.abs(delta) < 1) return 0;

    if (Math.abs(delta) > maxSafeDelta) {
      state.scrollAnchorRepair.missingAnchorBatches += 1;
      logLifecycle("scroll-anchor-compensation-skipped", { id: model.id, name: model.id, state: action }, {
        targetChunkId: model.id,
        action,
        phase,
        delta: Math.round(delta),
        reason: "unsafe-delta"
      });
      return 0;
    }

    state.scrollAnchorRepair.missingAnchorBatches += 1;

    logLifecycle("scroll-anchor-compensation-observed", { id: model.id, name: model.id, state: action }, {
      targetChunkId: model.id,
      action,
      phase,
      delta: Math.round(delta),
      reason: "programmatic-scroll-disabled"
    });

    return 0;
  }

  function withChunkScrollAnchorRepair(model, action, callback) {
    if (!shouldRepairChunkScrollAnchor(model)) {
      state.scrollAnchorRepair.skippedBatches += 1;
      return callback();
    }

    const upward = state.scrollVelocity < 0;
    const anchor = getScrollAnchorSnapshot(model);

    if (!anchor) {
      state.scrollAnchorRepair.missingAnchorBatches += 1;
      return callback();
    }

    const scrollingElement = document.scrollingElement || document.documentElement;
    const previousOverflowAnchor = scrollingElement.style.overflowAnchor;

    scrollingElement.style.overflowAnchor = "none";
    state.scrollAnchorRepair.batches += 1;
    if (upward) state.scrollAnchorRepair.upwardBatches += 1;

    const result = callback();

    compensateScrollAnchor(anchor, model, action, "immediate");
    window.requestAnimationFrame(() => {
      compensateScrollAnchor(anchor, model, action, "frame");
      scrollingElement.style.overflowAnchor = previousOverflowAnchor;
      state.scrollAnchorRepair.lastActionAt = new Date().toISOString();
    });

    state.scrollAnchorRepair.lastActionAt = new Date().toISOString();
    return result;
  }

  function isScrollIntegrityViewport() {
    const mobileSettings = getMobileInitialReductionConfig();
    return isMobileInitialReductionViewport(mobileSettings);
  }

  function recordScrollInput(type) {
    state.scrollIntegrity.lastInputAt = Date.now();
    state.scrollIntegrity.lastInputType = type;
  }

  function allowExplicitScrollIntent(duration = 1800) {
    state.scrollIntegrity.explicitIntentUntil = Math.max(
      state.scrollIntegrity.explicitIntentUntil || 0,
      Date.now() + duration
    );
  }

  function isExplicitScrollTrigger(target) {
    const trigger = target?.closest?.(
      "a[href], button, [role='button'], [data-eom-back-to-top], [data-back-to-top], [data-eom-allow-scroll], [data-format-gateway-card]"
    );

    if (!trigger) return false;
    if (trigger.matches("a[href]")) return true;
    if (trigger.matches("[data-eom-back-to-top], [data-back-to-top], [data-eom-allow-scroll], [data-format-gateway-card]")) return true;
    if (trigger.matches("button, [role='button']")) return true;

    return false;
  }

  function handleScrollIntegrity() {
    const integrityConfig = isPlainObject(config.scrollIntegrity) ? config.scrollIntegrity : DEFAULT_CONFIG.scrollIntegrity;

    if (integrityConfig.enabled === false || !isScrollIntegrityViewport() || window.Shopify?.designMode) {
      state.scrollIntegrity.lastTrustedScrollY = window.scrollY || 0;
      state.scrollIntegrity.lastTrustedScrollAt = Date.now();
      return;
    }

    const now = Date.now();
    const currentY = window.scrollY || 0;
    const previousY = state.scrollIntegrity.lastTrustedScrollY || 0;
    const delta = currentY - previousY;
    const absDelta = Math.abs(delta);
    const viewportHeight = window.innerHeight || 1;
    const jumpThreshold = Math.max(1400, viewportHeight * 2.25);
    const explicitIntent = now < (state.scrollIntegrity.explicitIntentUntil || 0);

    state.scrollIntegrity.lastDelta = Math.round(delta);

    if (absDelta > jumpThreshold && !explicitIntent) {
      state.scrollIntegrity.suspectedJumps += 1;
      state.scrollIntegrity.largestBlockedDelta = Math.max(state.scrollIntegrity.largestBlockedDelta || 0, Math.round(absDelta));
      state.scrollIntegrity.lastActionAt = new Date().toISOString();

      if (integrityConfig.monitorOnly === false) {
        state.scrollIntegrity.blockedJumps += 1;
        state.scrollIntegrity.corrections += 1;
      }

      logLifecycle("scroll-integrity-observed", { id: "scroll-integrity", name: "Scroll Integrity", state: "observed" }, {
        from: Math.round(previousY),
        to: Math.round(currentY),
        delta: Math.round(delta),
        monitorOnly: integrityConfig.monitorOnly !== false
      });

      if (integrityConfig.monitorOnly === false) return;
    }

    if (absDelta > jumpThreshold && explicitIntent) {
      state.scrollIntegrity.allowedLargeJumps += 1;
    }

    state.scrollIntegrity.lastTrustedScrollY = currentY;
    state.scrollIntegrity.lastTrustedScrollAt = now;
  }

  function offloadHomeChunk(model) {
    const record = getChunkRecord(model.id);
    let virtualized = 0;

    return withChunkScrollAnchorRepair(model, "offload", () => {
      model.sections.forEach((section) => {
        cleanupSectionBeforeVirtualize(section);

        if (window.EOMVirtualSections?.virtualize?.(section.root)) {
          virtualized += 1;
          cleanupSectionAfterVirtualize(section);
        }
      });

      const compactedArtifacts = model.safeToCompact ? compactHomeChunkArtifacts(model) : 0;
      const compactedSeoNodes = model.safeToCompact ? compactHomeChunkSeoShells(model) : 0;

      if (record.fetched || virtualized > 0 || compactedArtifacts > 0 || compactedSeoNodes > 0) {
        setChunkRecord(model.id, {
          state: "offloaded",
          mounted: false,
          offloaded: true,
          offloadedAt: new Date().toISOString(),
          error: "",
          mountedNodes: 0
        });

        logLifecycle("chunk-offloaded", { id: model.id, name: model.id, state: "offloaded" }, {
          targetChunkId: model.id,
          virtualizedSections: virtualized,
          compactedArtifacts,
          compactedSeoNodes
        });
      }

      return virtualized;
    });
  }

  function hydrateHomeChunk(model) {
    let hydrated = 0;

    return withChunkScrollAnchorRepair(model, "hydrate", () => {
      restoreHomeChunkSeoShells(model);
      restoreHomeChunkArtifacts(model);

      model.sections.forEach((section) => {
        restoreSectionMediaSources(section);

        if (window.EOMVirtualSections?.hydrate?.(section.root)) {
          restoreSectionMediaSources(section);
          releaseSectionHeightLock(section);
          hydrated += 1;
          return;
        }

        if (!["shell", "virtualized", "hydrating"].includes(section.state)) {
          releaseSectionHeightLock(section);
        }
      });

      if (hydrated > 0) {
        const record = getChunkRecord(model.id);
        setChunkRecord(model.id, {
          state: record.fetched ? "restored" : "mounted",
          mounted: true,
          offloaded: false,
          restored: record.offloaded || record.restored,
          restoredAt: record.offloaded ? new Date().toISOString() : record.restoredAt,
          restoredCount: record.offloaded ? (record.restoredCount || 0) + 1 : record.restoredCount,
          error: ""
        });

        logLifecycle("chunk-restored", { id: model.id, name: model.id, state: "restored" }, {
          targetChunkId: model.id,
          hydratedSections: hydrated
        });
      }

      return hydrated;
    });
  }

  function evaluateHomeChunkFetchManager(sections) {
    const settings = getChunkFetchManagerConfig();
    const viewportActive = isChunkFetchManagerViewport(settings);
    const result = {
      enabled: settings.enabled,
      viewportActive,
      fetchRequests: 0,
      hydrateRequests: 0,
      offloadRequests: 0
    };

    state.chunkFetchManager.enabled = settings.enabled;
    state.chunkFetchManager.viewportActive = viewportActive;

    if (!settings.enabled || !viewportActive || window.Shopify?.designMode) {
      state.chunkFetchManager.state = settings.enabled ? "idle" : "disabled";
      Object.assign(state.chunkFetchManager, summarizeChunkFetchManager());
      return result;
    }

    const models = buildChunkRuntimeModels(sections, settings);
    restoreMobileProtectedSeoShells(models);

    models.forEach((model) => {
      if (!model.mobileDeferred || !model.sectionCount || !model.fetchable) return;

      if (model.fetchNear || model.visiblePathProtected) {
        const record = getChunkRecord(model.id);
        if (!record.fetched && !record.fetching) {
          result.fetchRequests += 1;
          fetchHomeChunk(model.id, settings);
        }
      }

      if (model.hydrateNear || model.visiblePathProtected) {
        result.hydrateRequests += hydrateHomeChunk(model);
      } else if (model.farAway) {
        result.offloadRequests += offloadHomeChunk(model);
      } else {
        restoreHomeChunkSeoShells(model);
        restoreHomeChunkArtifacts(model);
        model.sections.forEach(releaseSectionHeightLock);
      }
    });

    Object.assign(state.chunkFetchManager, summarizeChunkFetchManager());

    if (state.chunkFetchManager.errorChunks > 0) {
      state.chunkFetchManager.state = "error";
    } else if (state.chunkFetchManager.fetchingChunks > 0) {
      state.chunkFetchManager.state = "fetching";
    } else if (state.chunkFetchManager.mountedChunks > 0) {
      state.chunkFetchManager.state = "mounted";
    } else if (state.chunkFetchManager.fetchedChunks > 0) {
      state.chunkFetchManager.state = "cached";
    } else {
      state.chunkFetchManager.state = "idle";
    }

    return result;
  }

  function getSectionThresholds(override = {}) {
    const adaptive = getAdaptiveThresholds();

    return {
      preloadDistance: toNumber(override.preloadDistance, adaptive.preloadDistance),
      offloadDistance: toNumber(override.offloadDistance, adaptive.offloadDistance),
      priority: toNumber(override.priority, 50)
    };
  }

  function getAdaptiveThresholds() {
    let preloadDistance = toNumber(config.preloadDistance, DEFAULT_CONFIG.preloadDistance);
    let offloadDistance = toNumber(config.offloadDistance, DEFAULT_CONFIG.offloadDistance);

    if (config.adaptiveBehavior === false) {
      return { preloadDistance, offloadDistance };
    }

    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const effectiveType = connection?.effectiveType || "";
    const saveData = Boolean(connection?.saveData);

    if (saveData || effectiveType.includes("2g") || effectiveType.includes("3g")) {
      preloadDistance = Math.round(preloadDistance * 1.35);
      offloadDistance = Math.round(offloadDistance * 1.15);
    }

    if (config.memoryPressureMode !== false && !(saveData || effectiveType.includes("2g") || effectiveType.includes("3g"))) {
      preloadDistance = Math.round(preloadDistance * 0.9);
      offloadDistance = Math.round(offloadDistance * 0.85);
    }

    if (Math.abs(state.scrollVelocity) > 2.4) {
      preloadDistance = Math.round(preloadDistance * 0.75);
    }

    return { preloadDistance, offloadDistance };
  }

  function getDistanceBand(rect, thresholds) {
    const viewportHeight = window.innerHeight || 1;
    const viewportAhead = toNumber(config.viewportWindow?.ahead, DEFAULT_CONFIG.viewportWindow.ahead);
    const viewportBehind = toNumber(config.viewportWindow?.behind, DEFAULT_CONFIG.viewportWindow.behind);
    const aheadLimit = Math.max(viewportHeight * viewportAhead, thresholds.preloadDistance);
    const behindLimit = Math.max(viewportHeight * viewportBehind, thresholds.offloadDistance);

    if (rect.bottom < -behindLimit) return "far-above";
    if (rect.bottom < 0) return "above";
    if (rect.top <= viewportHeight && rect.bottom >= 0) return "in-view";
    if (rect.top <= viewportHeight + aheadLimit) return "ahead";
    return "far-below";
  }

  function isInteractiveType(type) {
    const interactiveModes = Array.isArray(config.interactiveModes) ? config.interactiveModes : DEFAULT_CONFIG.interactiveModes;
    return interactiveModes.includes(type);
  }

  function isVisualType(type, mode) {
    const visualModes = Array.isArray(config.visualModes) ? config.visualModes : DEFAULT_CONFIG.visualModes;
    return visualModes.includes(type) || visualModes.includes(mode);
  }

  function getCrawlableSignals(section) {
    const cached = crawlableSignalCache.get(section);

    if (cached) return cached;

    const clone = section.cloneNode(true);

    clone.querySelectorAll(
      "template, [data-eom-virtual-mount], [data-eom-visual-mount], [data-eom-loader], [data-eom-visual-placeholder]"
    ).forEach((node) => node.remove());

    const text = (clone.textContent || "").replace(/\s+/g, " ").trim();
    const headings = clone.querySelectorAll("h1, h2, h3, h4, h5, h6").length;
    const links = clone.querySelectorAll("a[href]").length;
    const schemaScripts = section.querySelectorAll('script[type="application/ld+json"]').length;
    const formControls = clone.querySelectorAll("button, input, select, textarea").length;

    const signals = {
      textChars: text.length,
      headings,
      links,
      schemaScripts,
      formControls,
      hasCrawlableSignal: text.length > 40 || headings > 0 || links > 0 || schemaScripts > 0
    };

    crawlableSignalCache.set(section, signals);
    return signals;
  }

  function getDocumentCrawlableSignals() {
    const clone = document.body ? document.body.cloneNode(true) : document.createElement("body");

    clone.querySelectorAll(
      "template, [data-eom-virtual-mount], [data-eom-visual-mount], [data-eom-loader], [data-eom-visual-placeholder], #eom-home-chunk-fetch-poc-mount, #eom-home-chunk-cache-mount, #eom-home-chunk-placeholder-preview-layer, script:not([type='application/ld+json']), style"
    ).forEach((node) => node.remove());

    const text = (clone.textContent || "").replace(/\s+/g, " ").trim();
    const links = Array.from(clone.querySelectorAll("a[href]"));
    const internalLinks = links.filter((link) => {
      const href = link.getAttribute("href") || "";
      return href.startsWith("/") || href.includes(window.location.hostname);
    });
    const collectionLinks = links.filter((link) => {
      const href = link.getAttribute("href") || "";
      return href.includes("/collections/");
    });
    const h1Count = clone.querySelectorAll("h1").length;
    const schemaScripts = document.querySelectorAll('script[type="application/ld+json"]').length;
    const noJsBlocks = document.querySelectorAll("noscript").length;

    return {
      h1Count,
      textChars: text.length,
      links: links.length,
      internalLinks: internalLinks.length,
      collectionLinks: collectionLinks.length,
      schemaScripts,
      noJsBlocks,
      noJsFallbackSignals: noJsBlocks + h1Count + internalLinks.length + collectionLinks.length + schemaScripts
    };
  }

  function getMobileInitialReductionConfig() {
    const settings = isPlainObject(config.mobileInitialHtmlReduction)
      ? config.mobileInitialHtmlReduction
      : DEFAULT_CONFIG.mobileInitialHtmlReduction;

    return {
      enabled: settings.enabled !== false,
      maxWidth: toNumber(settings.maxWidth, DEFAULT_CONFIG.mobileInitialHtmlReduction.maxWidth),
      lowerChunkMinOrder: toNumber(settings.lowerChunkMinOrder, DEFAULT_CONFIG.mobileInitialHtmlReduction.lowerChunkMinOrder),
      protectChunkIds: Array.isArray(settings.protectChunkIds)
        ? settings.protectChunkIds
        : DEFAULT_CONFIG.mobileInitialHtmlReduction.protectChunkIds,
      domNodeBudget: toNumber(settings.domNodeBudget, DEFAULT_CONFIG.mobileInitialHtmlReduction.domNodeBudget),
      activeSectionBudget: toNumber(settings.activeSectionBudget, DEFAULT_CONFIG.mobileInitialHtmlReduction.activeSectionBudget)
    };
  }

  function isMobileInitialReductionViewport(settings = getMobileInitialReductionConfig()) {
    return window.innerWidth <= settings.maxWidth;
  }

  function isProtectedMobileChunk(section, settings = getMobileInitialReductionConfig()) {
    return section.type === "critical" ||
      section.homeChunkStrategy === "initial" ||
      settings.protectChunkIds.includes(section.homeChunkId);
  }

  function isMobileLowerChunkCandidate(section, settings = getMobileInitialReductionConfig()) {
    return settings.enabled &&
      isMobileInitialReductionViewport(settings) &&
      section.managedByWindow &&
      section.state === "active" &&
      section.distanceBand === "far-below" &&
      section.homeChunkFetchable === true &&
      toNumber(section.homeChunkOrder, 0) >= settings.lowerChunkMinOrder &&
      !isProtectedMobileChunk(section, settings);
  }

  function getSectionSnapshot(section, index) {
    const rect = section.getBoundingClientRect();
    const mode = section.dataset.eomVirtualMode || "unknown";
    const virtualState = section.dataset.eomVirtualState || "unknown";
    const enabled = section.dataset.eomEnableVirtualization !== "false";
    const nearViewport = section.dataset.eomNearViewport === "true";
    const farFromViewport = section.dataset.eomFarFromViewport === "true";
    const override = getSectionOverride(section, index);
    const thresholds = getSectionThresholds(override);
    const type = getSectionType(section, mode, override);
    const homeChunk = resolveHomeChunk(section, index, type, override);
    const crawlable = getCrawlableSignals(section);

    stampHomeChunkAttributes(section, homeChunk);

    const mounts = Array.from(section.querySelectorAll("[data-eom-virtual-mount], [data-eom-visual-mount]"));
    const placeholders = Array.from(section.querySelectorAll("[data-eom-loader], [data-eom-visual-placeholder]"));
    const templates = Array.from(section.querySelectorAll("[data-eom-virtual-template], [data-eom-visual-template]"));

    const mountedDomNodes = mounts.reduce((sum, mount) => sum + mount.querySelectorAll("*").length, 0);
    const mountedChildren = mounts.reduce((sum, mount) => sum + mount.children.length, 0);
    const placeholderDomNodes = placeholders.reduce((sum, placeholder) => sum + placeholder.querySelectorAll("*").length, 0);
    const templateChildren = templates.reduce((sum, template) => {
      return sum + (template.content ? template.content.children.length : 0);
    }, 0);

    return {
      root: section,
      index,
      id: getSectionId(section, index),
      name: getSectionName(section, index),
      mode,
      type,
      homeChunkId: homeChunk.id,
      homeChunkLabel: homeChunk.label,
      homeChunkOrder: homeChunk.order,
      homeChunkStrategy: homeChunk.strategy,
      homeChunkRole: homeChunk.role,
      homeChunkFetchable: homeChunk.fetchable,
      homeChunkSource: homeChunk.source,
      state: virtualState,
      enabled,
      managedByWindow: enabled && mode !== "static" && type !== "footer-static",
      interactive: isInteractiveType(type),
      visual: isVisualType(type, mode),
      nearViewport,
      farFromViewport,
      distanceBand: getDistanceBand(rect, thresholds),
      height: Math.round(rect.height),
      top: Math.round(rect.top),
      bottom: Math.round(rect.bottom),
      loadMargin: section.dataset.eomLoadMargin || "",
      virtualizeDistance: section.dataset.eomVirtualizeDistance || "",
      preloadDistance: thresholds.preloadDistance,
      offloadDistance: thresholds.offloadDistance,
      priority: thresholds.priority,
      crawlableTextChars: crawlable.textChars,
      crawlableHeadings: crawlable.headings,
      crawlableLinks: crawlable.links,
      schemaScripts: crawlable.schemaScripts,
      crawlableFormControls: crawlable.formControls,
      hasCrawlableSignal: crawlable.hasCrawlableSignal,
      mountedChildren,
      mountedDomNodes,
      placeholderDomNodes,
      templateChildren
    };
  }

  function rememberLifecycle(sections) {
    const currentIds = new Set(sections.map((section) => section.id));

    sections.forEach((section) => {
      const previous = previousById.get(section.id);
      const memory = sectionMemory.get(section.id) || {};

      if (!previous) {
        logLifecycle("discovered", section);
      }

      if (section.nearViewport && !previous?.nearViewport) {
        logLifecycle("near-viewport", section);
      }

      if (section.state === "active" && previous?.state !== "active") {
        logLifecycle(memory.wasShell ? "restored" : "active", section);
      }

      if (section.state === "shell" && previous?.state !== "shell") {
        logLifecycle("shell", section);
        memory.wasShell = true;
      }

      if ((section.distanceBand === "far-above" || section.distanceBand === "far-below") && previous?.distanceBand !== section.distanceBand) {
        logLifecycle("offloaded", section, { reason: section.distanceBand });
      }

      if (previous && Math.abs(section.height - previous.height) > toNumber(config.layoutShiftTolerance, DEFAULT_CONFIG.layoutShiftTolerance)) {
        logLifecycle("layout-height-change", section, {
          fromHeight: previous.height,
          toHeight: section.height,
          delta: section.height - previous.height
        });
      }

      sectionMemory.set(section.id, memory);
    });

    previousById.forEach((previous, id) => {
      if (!currentIds.has(id)) {
        logLifecycle("removed", previous);
      }
    });

    previousById = new Map(sections.map((section) => [section.id, { ...section, root: null }]));
  }

  function sectionSortDistance(section) {
    if (section.distanceBand === "in-view") return 0;
    if (section.distanceBand === "ahead") return Math.max(section.top, 0);
    if (section.distanceBand === "above") return Math.abs(section.bottom);
    return Number.MAX_SAFE_INTEGER - section.priority;
  }

  function applyControlledActivation(sections) {
    const mobileReductionSettings = getMobileInitialReductionConfig();
    const mobileReductionViewport = isMobileInitialReductionViewport(mobileReductionSettings);
    let mobileInitialReductionEligible = 0;
    let mobileInitialReductionVirtualized = 0;
    let mobileInitialReductionProtected = 0;
    let mobileInitialReductionHeroProtected = false;

    if (!state.enabled || config.controlActivation === false || !window.EOMVirtualSections) {
      return {
        enabled: false,
        hydrateRequests: 0,
        virtualizeRequests: 0,
        skippedHydrateRequests: 0,
        pressureSkippedHydrateRequests: 0,
        nearestUpcomingId: "",
        mobileInitialReductionEnabled: mobileReductionSettings.enabled,
        mobileInitialReductionViewport: mobileReductionViewport,
        mobileInitialReductionEligible,
        mobileInitialReductionVirtualized,
        mobileInitialReductionProtected,
        mobileInitialReductionHeroProtected
      };
    }

    const managed = sections.filter((section) => section.managedByWindow);
    const inView = managed.filter((section) => section.distanceBand === "in-view");
    const ahead = managed.filter((section) => section.distanceBand === "ahead").sort((a, b) => a.top - b.top);
    const nearestUpcoming = ahead[0] || null;
    const hydrateAhead = Math.abs(state.scrollVelocity) <= 2.4 || !state.startupSweepComplete;
    const hydrateSet = new Set([...inView, ...(nearestUpcoming && hydrateAhead ? [nearestUpcoming] : [])]);
    const candidates = [...hydrateSet].sort((a, b) => {
      if (a.distanceBand !== b.distanceBand) {
        if (a.distanceBand === "in-view") return -1;
        if (b.distanceBand === "in-view") return 1;
      }
      return sectionSortDistance(a) - sectionSortDistance(b);
    });

    let activeVisual = managed.filter((section) => section.state === "active" && section.visual).length;
    let activeInteractive = managed.filter((section) => section.state === "active" && section.interactive).length;
    let activeManaged = managed.filter((section) => section.state === "active").length;
    let hydrateRequests = 0;
    let skippedHydrateRequests = 0;
    let virtualizeRequests = 0;
    let pressureSkippedHydrateRequests = 0;
    const maxVisual = toNumber(config.maxActiveVisualSections, DEFAULT_CONFIG.maxActiveVisualSections);
    const maxInteractive = toNumber(config.maxActiveInteractiveSections, DEFAULT_CONFIG.maxActiveInteractiveSections);
    const maxManaged = toNumber(config.maxActiveManagedSections, DEFAULT_CONFIG.maxActiveManagedSections);

    candidates.forEach((section) => {
      if (section.state === "active" || section.state === "hydrating") return;

      const visualAllowed = !section.visual || activeVisual < maxVisual || section.distanceBand === "in-view" || section.type === "critical";
      const interactiveAllowed = !section.interactive || activeInteractive < maxInteractive || section.distanceBand === "in-view";
      const managedAllowed = config.memoryPressureMode === false ||
        activeManaged < maxManaged ||
        section.distanceBand === "in-view" ||
        section.type === "critical";

      if (!visualAllowed || !interactiveAllowed || !managedAllowed) {
        skippedHydrateRequests += 1;
        if (!managedAllowed) pressureSkippedHydrateRequests += 1;
        logLifecycle("hydrate-skipped", section, { visualAllowed, interactiveAllowed, managedAllowed });
        return;
      }

      restoreSectionMediaSources(section);

      if (window.EOMVirtualSections.hydrate?.(section.root)) {
        restoreSectionMediaSources(section);
        hydrateRequests += 1;
        activeManaged += 1;
        if (section.visual) activeVisual += 1;
        if (section.interactive) activeInteractive += 1;
        logLifecycle("hydrate-requested", section);
      }
    });

    managed.forEach((section) => {
      const farAway = section.distanceBand === "far-above" || section.distanceBand === "far-below";
      const outsideWindow = farAway;

      if (!outsideWindow || section.state !== "active") return;
      if (section.type === "critical" && section.distanceBand === "above") return;
      if (isMobileVisiblePathProtectedSection(section)) return;

      cleanupSectionBeforeVirtualize(section);

      if (window.EOMVirtualSections.virtualize?.(section.root)) {
        virtualizeRequests += 1;
        logLifecycle("virtualize-requested", section, { reason: section.distanceBand });
        cleanupSectionAfterVirtualize(section);
      }
    });

    if (!state.startupSweepComplete && config.initialLoadReduction !== false) {
      managed.forEach((section) => {
        const protectedMobileChunk = isProtectedMobileChunk(section, mobileReductionSettings);
        const mobileLowerCandidate = isMobileLowerChunkCandidate(section, mobileReductionSettings);
        const shouldStartShell = section.state === "active" &&
          section.distanceBand === "far-below" &&
          section.type !== "critical";

        if (mobileReductionViewport && protectedMobileChunk) {
          mobileInitialReductionProtected += 1;
          if (section.type === "critical" || section.homeChunkId === "home-initial") {
            mobileInitialReductionHeroProtected = true;
          }
        }

        if (mobileLowerCandidate) {
          mobileInitialReductionEligible += 1;
        }

        if (!shouldStartShell) return;
        if (mobileReductionViewport && protectedMobileChunk) return;

        cleanupSectionBeforeVirtualize(section);

        if (window.EOMVirtualSections.virtualize?.(section.root)) {
          virtualizeRequests += 1;
          if (mobileLowerCandidate) mobileInitialReductionVirtualized += 1;
          logLifecycle("startup-virtualize-requested", section);
          cleanupSectionAfterVirtualize(section);
        }
      });
      state.startupSweepComplete = true;
    }

    return {
      enabled: true,
      hydrateRequests,
      virtualizeRequests,
      skippedHydrateRequests,
      pressureSkippedHydrateRequests,
      nearestUpcomingId: nearestUpcoming?.id || "",
      mobileInitialReductionEnabled: mobileReductionSettings.enabled,
      mobileInitialReductionViewport: mobileReductionViewport,
      mobileInitialReductionEligible,
      mobileInitialReductionVirtualized,
      mobileInitialReductionProtected,
      mobileInitialReductionHeroProtected
    };
  }

  function isMediaCleanupProtected(section) {
    return !section?.root ||
      section.type === "critical" ||
      section.homeChunkId === "home-initial" ||
      section.root.dataset.eomMediaCleanupProtect === "true";
  }

  function getMediaCleanupTargets(section) {
    if (!section?.root) return [];

    const targets = Array.from(section.root.querySelectorAll(
      "[data-eom-virtual-mount] img, [data-eom-virtual-mount] source, [data-eom-visual-mount] img, [data-eom-visual-mount] source"
    ));

    section.root.querySelectorAll("[data-eom-virtual-template], [data-eom-visual-template]").forEach((template) => {
      if (!template.content) return;
      targets.push(...Array.from(template.content.querySelectorAll("img, source")));
    });

    return targets;
  }

  function rememberMediaSource(media) {
    if (!media?.dataset) return;
    if (media.dataset.eomOriginalSrc === undefined) media.dataset.eomOriginalSrc = media.getAttribute("src") || "";
    if (media.dataset.eomOriginalSrcset === undefined) media.dataset.eomOriginalSrcset = media.getAttribute("srcset") || "";
    if (media.dataset.eomOriginalSizes === undefined) media.dataset.eomOriginalSizes = media.getAttribute("sizes") || "";
  }

  function restoreAttribute(media, name, dataKeys) {
    const value = dataKeys.map((key) => media.dataset[key]).find((candidate) => candidate !== undefined && candidate !== "");

    if (value === undefined) return false;
    if (media.getAttribute(name) === value) return false;

    media.setAttribute(name, value);
    return true;
  }

  function stripSectionMediaSources(section) {
    if (config.imageMemoryCleanup === false || window.Shopify?.designMode) return 0;
    if (!isScrollIntegrityViewport()) return 0;
    if (isMediaCleanupProtected(section)) {
      state.mediaCleanup.protectedSections += 1;
      return 0;
    }

    let stripped = 0;

    getMediaCleanupTargets(section).forEach((media) => {
      const hadSource = media.hasAttribute("src") || media.hasAttribute("srcset") || media.hasAttribute("sizes");
      if (!hadSource) return;

      rememberMediaSource(media);
      media.removeAttribute("src");
      media.removeAttribute("srcset");
      media.removeAttribute("sizes");
      media.dataset.eomMediaSourceStripped = "true";
      stripped += 1;
    });

    if (stripped > 0) {
      const previousStripped = toNumber(section.root.dataset.eomMediaStrippedCount, 0);

      section.root.dataset.eomMediaSourcesStripped = "true";
      section.root.dataset.eomMediaStrippedCount = String(previousStripped + stripped);
      if (previousStripped === 0) state.mediaCleanup.currentStrippedSections += 1;
      state.mediaCleanup.currentStrippedMedia += stripped;
      state.mediaCleanup.totalStrippedSections += 1;
      state.mediaCleanup.totalStrippedMedia += stripped;
      state.mediaCleanup.lastActionAt = new Date().toISOString();
    }

    return stripped;
  }

  function restoreSectionMediaSources(section) {
    if (!section?.root) return 0;

    let restored = 0;
    const previousStripped = toNumber(section.root.dataset.eomMediaStrippedCount, 0);

    getMediaCleanupTargets(section).forEach((media) => {
      const restoredSrc = restoreAttribute(media, "src", ["eomOriginalSrc", "eomChunkOriginalSrc"]);
      const restoredSrcset = restoreAttribute(media, "srcset", ["eomOriginalSrcset", "eomChunkOriginalSrcset"]);
      const restoredSizes = restoreAttribute(media, "sizes", ["eomOriginalSizes", "eomChunkOriginalSizes"]);

      if (restoredSrc || restoredSrcset || restoredSizes) {
        delete media.dataset.eomMediaSourceStripped;
        restored += 1;
      }
    });

    if (restored > 0 || previousStripped > 0) {
      state.mediaCleanup.currentStrippedSections = Math.max(state.mediaCleanup.currentStrippedSections - 1, 0);

      delete section.root.dataset.eomMediaSourcesStripped;
      delete section.root.dataset.eomMediaStrippedCount;
      state.mediaCleanup.currentStrippedMedia = Math.max(state.mediaCleanup.currentStrippedMedia - Math.max(restored, previousStripped), 0);
      state.mediaCleanup.totalRestoredSections += 1;
      state.mediaCleanup.totalRestoredMedia += Math.max(restored, previousStripped);
      state.mediaCleanup.lastActionAt = new Date().toISOString();
    }

    return restored;
  }

  function cleanupSectionBeforeVirtualize(section) {
    stripSectionMediaSources(section);
  }

  function cleanupSectionAfterVirtualize(section) {
    if (config.strongCleanup === false || !section?.root) return;

    section.root.querySelectorAll("[data-eom-virtual-mount], [data-eom-visual-mount]").forEach((mount) => {
      if (mount.childElementCount) mount.replaceChildren();
    });
  }

  function summarizeLifecycle() {
    const counts = state.lifecycle.reduce((acc, event) => {
      acc[event.type] = (acc[event.type] || 0) + 1;
      return acc;
    }, {});

    return {
      total: state.lifecycle.length,
      counts,
      discovered: counts.discovered || 0,
      nearViewport: counts["near-viewport"] || 0,
      active: counts.active || 0,
      shell: counts.shell || 0,
      offloaded: counts.offloaded || 0,
      restored: counts.restored || 0,
      hydrateRequested: counts["hydrate-requested"] || 0,
      virtualizeRequested: counts["virtualize-requested"] || 0,
      layoutHeightChange: counts["layout-height-change"] || 0
    };
  }

  function buildSummary(sections, controlResult) {
    const activeVisual = sections.filter((section) => section.state === "active" && section.visual && section.managedByWindow).length;
    const activeInteractive = sections.filter((section) => section.state === "active" && section.interactive && section.managedByWindow).length;
    const activeManaged = sections.filter((section) => section.state === "active" && section.managedByWindow).length;
    const outOfViewActiveManaged = sections.filter((section) => {
      return section.state === "active" &&
        section.managedByWindow &&
        section.distanceBand !== "in-view";
    }).length;
    const staticSections = sections.filter((section) => section.mode === "static" || section.type === "footer-static");
    const shellSections = sections.filter((section) => section.state === "shell");
    const controlledCandidates = sections.filter((section) => section.managedByWindow);
    const shellWithoutCrawlableSignals = shellSections.filter((section) => !section.hasCrawlableSignal).length;
    const priorityTypes = Array.isArray(config.priorityTypes) ? config.priorityTypes : DEFAULT_CONFIG.priorityTypes;
    const unknownPrioritySections = sections.filter((section) => !priorityTypes.includes(section.type)).length;
    const farManagedActive = controlledCandidates.filter((section) => {
      return section.state === "active" && (section.distanceBand === "far-above" || section.distanceBand === "far-below");
    }).length;
    const lifecycle = summarizeLifecycle();
    const chunkPlaceholderModels = Array.isArray(state.chunkPlaceholders) ? state.chunkPlaceholders : [];
    const chunkFetchPoc = state.chunkFetchPoc || {};
    const chunkFetchManager = state.chunkFetchManager || {};
    const chunkHeightReservations = state.chunkHeightReservations || {};
    const chunkFetchRecords = Object.values(chunkFetchManager.chunks || {});
    const configuredChunks = Array.isArray(config.homeChunks) ? config.homeChunks : [];
    const chunkIds = new Set(sections.map((section) => section.homeChunkId).filter(Boolean));
    const chunkCandidateSections = sections.filter((section) => section.homeChunkId).length;
    const chunkUnknownSections = sections.filter((section) => !section.homeChunkId).length;
    const chunkFetchableSections = sections.filter((section) => section.homeChunkFetchable).length;
    const chunkInitialSections = sections.filter((section) => section.homeChunkStrategy === "initial").length;
    const seoSignals = getDocumentCrawlableSignals();
    const seoMinimums = isPlainObject(config.seoNoJsMinimums) ? config.seoNoJsMinimums : DEFAULT_CONFIG.seoNoJsMinimums;
    const mobileReductionSettings = getMobileInitialReductionConfig();
    const mobileReductionViewport = isMobileInitialReductionViewport(mobileReductionSettings);
    const mobileProtectedSections = sections.filter((section) => isProtectedMobileChunk(section, mobileReductionSettings));
    const mobileLowerSections = sections.filter((section) => {
      return section.homeChunkFetchable === true &&
        toNumber(section.homeChunkOrder, 0) >= mobileReductionSettings.lowerChunkMinOrder &&
        !isProtectedMobileChunk(section, mobileReductionSettings);
    });
    const mobileLowerActiveSections = mobileLowerSections.filter((section) => section.state === "active").length;
    const mobileLowerShellSections = mobileLowerSections.filter((section) => section.state === "shell").length;
    const mobileSeoShellProtectedSections = mobileReductionViewport
      ? sections.filter(isMobileSeoShellCompactionProtected)
      : [];
    const mobileSeoShellProtectedChunkIds = Array.from(new Set(
      mobileSeoShellProtectedSections.map((section) => section.homeChunkId).filter(Boolean)
    ));
    const mobileSeoShellProtectedCompactedSections = mobileSeoShellProtectedSections.filter((section) => {
      return section.root?.dataset?.eomSeoShellCompacted === "true";
    }).length;
    const visiblePathViewportHeight = getStableMobileViewportHeight();
    const mobileVisiblePathProtectedSections = mobileReductionViewport
      ? sections.filter((section) => isMobileVisiblePathProtectedSection(section, visiblePathViewportHeight))
      : [];
    const mobileVisiblePathProtectedShellSections = mobileVisiblePathProtectedSections.filter((section) => {
      return section.state === "shell" || isSectionHeightLocked(section.root);
    });
    const mobileVisiblePathProtectedChunkIds = Array.from(new Set(
      mobileVisiblePathProtectedSections.map((section) => section.homeChunkId).filter(Boolean)
    ));
    const mobileStableVisualSections = sections.filter((section) => {
      return section.root?.dataset?.eomMobileStableVisual === "true";
    });
    const mobileStableVisualProblemSections = mobileStableVisualSections.filter((section) => {
      return section.enabled !== false ||
        section.state !== "active" ||
        isSectionHeightLocked(section.root) ||
        section.root?.dataset?.eomHomeChunkReservedByManager === "true";
    });
    const repeatDeliverySections = sections.filter((section) => {
      const className = String(section.root?.className || section.name || "");
      return section.root?.dataset?.eomSectionKind === "featured-repeat-delivery" ||
        className.includes("repeat-sub-section__") ||
        className.includes("featured_subscription_plan");
    });
    const repeatDeliveryHeightLockedSections = repeatDeliverySections.filter((section) => {
      return isSectionHeightLocked(section.root) || section.root?.dataset?.eomHomeChunkReservedByManager === "true";
    });
    const repeatDeliveryVisualOnlySections = repeatDeliverySections.filter((section) => {
      return section.root?.dataset?.eomVisualOnly === "true" ||
        section.root?.dataset?.eomNoHeightLock === "true";
    });
    const repeatDeliveryChunkReservationApplied = (chunkHeightReservations.appliedReservationChunkIds || []).includes("home-subscribe-value");
    const repeatDeliveryChunkReservationSkipped = (chunkHeightReservations.cleanContractSkippedChunkIds || []).includes("home-subscribe-value");
    const realFetchedChunks = chunkFetchManager.fetchedChunks || chunkFetchRecords.filter((record) => record.fetched).length;
    const fetchedChunks = realFetchedChunks || (chunkFetchPoc.state === "mounted" || chunkFetchPoc.state === "restored" ? 1 : 0);
    const initialDomNodes = document.querySelectorAll("*").length;
    const mobileLaunchBudgetPass = !mobileReductionViewport || (
      initialDomNodes <= mobileReductionSettings.domNodeBudget &&
      activeManaged <= mobileReductionSettings.activeSectionBudget &&
      farManagedActive === 0
    );
    const placeholderByChunkId = new Map(
      chunkPlaceholderModels.map((placeholder) => [placeholder.id, placeholder])
    );
    const chunkDetails = configuredChunks.map((chunk) => {
      const chunkSections = sections.filter((section) => section.homeChunkId === chunk.id);
      const order = toNumber(chunk.order, 0);
      const initial = chunk.strategy === "initial" || order < mobileReductionSettings.lowerChunkMinOrder;
      const mobileDeferred = mobileReductionSettings.enabled &&
        !initial &&
        Boolean(chunk.fetchable) &&
        !mobileReductionSettings.protectChunkIds.includes(chunk.id);
      const crawlableTextChars = chunkSections.reduce((sum, section) => sum + section.crawlableTextChars, 0);
      const crawlableLinks = chunkSections.reduce((sum, section) => sum + section.crawlableLinks, 0);
      const crawlableHeadings = chunkSections.reduce((sum, section) => sum + section.crawlableHeadings, 0);
      const schemaScripts = chunkSections.reduce((sum, section) => sum + section.schemaScripts, 0);
      const seoPreserved = initial ||
        crawlableTextChars > 120 ||
        crawlableLinks > 0 ||
        crawlableHeadings > 0 ||
        schemaScripts > 0;
      const placeholder = placeholderByChunkId.get(chunk.id);
      const heightStable = Boolean(placeholder?.stable);
      const fetchRecord = chunkFetchManager.chunks?.[chunk.id] || null;
      const heightRecord = chunkHeightReservations.chunks?.[chunk.id] || null;

      return {
        id: chunk.id,
        label: chunk.label || chunk.id,
        order,
        strategy: chunk.strategy || "",
        role: chunk.role || "",
        fetchable: Boolean(chunk.fetchable),
        initial,
        mobileDeferred,
        seoPreserved,
        heightStable,
        fetchState: fetchRecord?.state || "pending",
        fetched: Boolean(fetchRecord?.fetched),
        mountedFromCache: Boolean(fetchRecord?.mounted),
        offloadedByChunkManager: Boolean(fetchRecord?.offloaded),
        requestCount: fetchRecord?.requestCount || 0,
        reservedHeight: heightRecord?.reservedHeight || placeholder?.height || 0,
        measuredHeight: heightRecord?.measuredHeight || 0,
        estimatedHeight: heightRecord?.estimatedHeight || 0,
        heightSource: heightRecord?.source || "unknown",
        heightReservedByManager: Boolean(heightRecord),
        heightStableByManager: Boolean(heightRecord?.stable),
        heightAppliedToLayout: Boolean(heightRecord?.appliedToLayout),
        activeReservationReleased: Boolean(heightRecord?.activeReservationReleased),
        sectionCount: chunkSections.length,
        active: chunkSections.filter((section) => section.state === "active").length,
        shell: chunkSections.filter((section) => section.state === "shell").length,
        crawlableTextChars,
        crawlableLinks,
        crawlableHeadings,
        schemaScripts
      };
    });
    const chunkContractReady = chunkDetails.length === configuredChunks.length &&
      chunkDetails.length > 0 &&
      chunkDetails.every((chunk) => {
        return chunk.sectionCount > 0 &&
          typeof chunk.initial === "boolean" &&
          typeof chunk.mobileDeferred === "boolean" &&
          typeof chunk.seoPreserved === "boolean" &&
          typeof chunk.fetchable === "boolean" &&
          chunk.heightStable === true;
      });
    const chunkInitialCount = chunkDetails.filter((chunk) => chunk.initial).length;
    const chunkMobileDeferredCount = chunkDetails.filter((chunk) => chunk.mobileDeferred).length;
    const chunkSeoPreservedCount = chunkDetails.filter((chunk) => chunk.seoPreserved).length;
    const chunkFetchableCount = chunkDetails.filter((chunk) => chunk.fetchable).length;
    const chunkHeightStableCount = chunkDetails.filter((chunk) => chunk.heightStable).length;
    const realMountedChunks = chunkFetchManager.mountedChunks || chunkFetchRecords.filter((record) => record.mounted).length;
    const mountedChunks = Math.max(
      chunkDetails.filter((chunk) => chunk.active > 0).length,
      realMountedChunks
    ) + (chunkFetchPoc.mounted && !realMountedChunks ? 1 : 0);
    const productDiscoveryChunk = chunkDetails.find((chunk) => chunk.id === "home-product-discovery") || null;
    const productDiscoverySections = sections.filter((section) => section.homeChunkId === "home-product-discovery");
    const productDiscoveryActive = productDiscoverySections.filter((section) => section.state === "active").length;
    const productDiscoveryShell = productDiscoverySections.filter((section) => section.state === "shell").length;
    const productDiscoveryMobileEligible = mobileReductionSettings.enabled &&
      mobileReductionViewport &&
      !mobileReductionSettings.protectChunkIds.includes("home-product-discovery") &&
      productDiscoverySections.some((section) => {
        return section.homeChunkId === "home-product-discovery" &&
          toNumber(section.homeChunkOrder, 0) >= mobileReductionSettings.lowerChunkMinOrder;
      });

    return {
      total: sections.length,
      active: sections.filter((section) => section.state === "active").length,
      shell: sections.filter((section) => section.state === "shell").length,
      hydrating: sections.filter((section) => section.state === "hydrating").length,
      static: staticSections.length,
      visual: sections.filter((section) => section.type === "visual").length,
      interactive: sections.filter((section) => section.type === "interactive").length,
      cardGrid: sections.filter((section) => section.type === "card-grid").length,
      critical: sections.filter((section) => section.type === "critical").length,
      footerStatic: sections.filter((section) => section.type === "footer-static").length,
      template: sections.filter((section) => section.mode === "template").length,
      custom: sections.filter((section) => section.mode === "custom").length,
      enabled: sections.filter((section) => section.enabled).length,
      disabled: sections.filter((section) => !section.enabled).length,
      managedByWindow: controlledCandidates.length,
      inView: sections.filter((section) => section.distanceBand === "in-view").length,
      ahead: sections.filter((section) => section.distanceBand === "ahead").length,
      above: sections.filter((section) => section.distanceBand === "above").length,
      farAbove: sections.filter((section) => section.distanceBand === "far-above").length,
      farBelow: sections.filter((section) => section.distanceBand === "far-below").length,
      activeVisual,
      activeInteractive,
      activeManaged,
      outOfViewActiveManaged,
      maxActiveManagedSections: toNumber(config.maxActiveManagedSections, DEFAULT_CONFIG.maxActiveManagedSections),
      maxOutOfViewActiveManagedSections: toNumber(config.maxOutOfViewActiveManagedSections, DEFAULT_CONFIG.maxOutOfViewActiveManagedSections),
      maxActiveVisualSections: toNumber(config.maxActiveVisualSections, DEFAULT_CONFIG.maxActiveVisualSections),
      maxActiveInteractiveSections: toNumber(config.maxActiveInteractiveSections, DEFAULT_CONFIG.maxActiveInteractiveSections),
      activeManagedOverLimit: activeManaged > toNumber(config.maxActiveManagedSections, DEFAULT_CONFIG.maxActiveManagedSections),
      outOfViewActiveManagedOverLimit: outOfViewActiveManaged > toNumber(config.maxOutOfViewActiveManagedSections, DEFAULT_CONFIG.maxOutOfViewActiveManagedSections),
      activeVisualOverLimit: activeVisual > toNumber(config.maxActiveVisualSections, DEFAULT_CONFIG.maxActiveVisualSections),
      activeInteractiveOverLimit: activeInteractive > toNumber(config.maxActiveInteractiveSections, DEFAULT_CONFIG.maxActiveInteractiveSections),
      farManagedActive,
      staticIgnored: staticSections.every((section) => !section.managedByWindow),
      priorityModelReady: unknownPrioritySections === 0,
      unknownPrioritySections,
      crawlableSignalSections: sections.filter((section) => section.hasCrawlableSignal).length,
      shellWithCrawlableSignals: shellSections.filter((section) => section.hasCrawlableSignal).length,
      shellWithoutCrawlableSignals,
      crawlableTextChars: sections.reduce((sum, section) => sum + section.crawlableTextChars, 0),
      crawlableLinks: sections.reduce((sum, section) => sum + section.crawlableLinks, 0),
      crawlableHeadings: sections.reduce((sum, section) => sum + section.crawlableHeadings, 0),
      schemaScripts: sections.reduce((sum, section) => sum + section.schemaScripts, 0),
      seoH1Count: seoSignals.h1Count,
      seoCrawlableTextChars: seoSignals.textChars,
      seoCrawlableLinks: seoSignals.links,
      seoImportantInternalLinks: seoSignals.internalLinks,
      seoMajorCollectionLinks: seoSignals.collectionLinks,
      seoSchemaScripts: seoSignals.schemaScripts,
      seoNoJsBlocks: seoSignals.noJsBlocks,
      seoNoJsFallbackSignals: seoSignals.noJsFallbackSignals,
      seoH1Pass: seoSignals.h1Count >= toNumber(seoMinimums.h1, DEFAULT_CONFIG.seoNoJsMinimums.h1),
      seoTextPass: seoSignals.textChars >= toNumber(seoMinimums.crawlableTextChars, DEFAULT_CONFIG.seoNoJsMinimums.crawlableTextChars),
      seoLinksPass: seoSignals.links >= toNumber(seoMinimums.crawlableLinks, DEFAULT_CONFIG.seoNoJsMinimums.crawlableLinks),
      seoImportantInternalLinksPass: seoSignals.internalLinks >= toNumber(seoMinimums.importantInternalLinks, DEFAULT_CONFIG.seoNoJsMinimums.importantInternalLinks),
      seoMajorCollectionLinksPass: seoSignals.collectionLinks >= toNumber(seoMinimums.majorCollectionLinks, DEFAULT_CONFIG.seoNoJsMinimums.majorCollectionLinks),
      seoSchemaPass: seoSignals.schemaScripts >= toNumber(seoMinimums.schemaScripts, DEFAULT_CONFIG.seoNoJsMinimums.schemaScripts),
      seoNoJsFallbackPass: seoSignals.noJsFallbackSignals > 0 &&
        seoSignals.h1Count >= toNumber(seoMinimums.h1, DEFAULT_CONFIG.seoNoJsMinimums.h1) &&
        seoSignals.internalLinks >= toNumber(seoMinimums.importantInternalLinks, DEFAULT_CONFIG.seoNoJsMinimums.importantInternalLinks) &&
        seoSignals.collectionLinks >= toNumber(seoMinimums.majorCollectionLinks, DEFAULT_CONFIG.seoNoJsMinimums.majorCollectionLinks),
      mountedDomNodes: sections.reduce((sum, section) => sum + section.mountedDomNodes, 0),
      placeholderDomNodes: sections.reduce((sum, section) => sum + section.placeholderDomNodes, 0),
      homeChunkPlaceholderPreviewEnabled: shouldShowChunkPlaceholderPreview(),
      homeChunkPlaceholders: chunkPlaceholderModels.length,
      homeChunkPlaceholderVisible: chunkPlaceholderModels.filter((model) => model.previewVisible && model.stable).length,
      homeChunkPlaceholderStableHeights: chunkPlaceholderModels.filter((model) => model.stable).length,
      homeChunkPlaceholderUnknownHeights: chunkPlaceholderModels.filter((model) => !model.stable).length,
      homeChunkPlaceholderReservedHeight: chunkPlaceholderModels.reduce((sum, model) => sum + model.height, 0),
      homeChunkPlaceholderDebugOnly: true,
      homeChunkPlaceholderPassive: true,
      homeChunksConfigured: configuredChunks.length,
      homeChunksDiscovered: chunkIds.size,
      homeChunkCandidateSections: chunkCandidateSections,
      homeChunkUnknownSections: chunkUnknownSections,
      homeChunkInitialSections: chunkInitialSections,
      homeChunkFetchableSections: chunkFetchableSections,
      homeChunkPassiveReady: configuredChunks.length > 0 && chunkCandidateSections === sections.length && chunkUnknownSections === 0,
      homeChunkContractReady: chunkContractReady,
      homeChunkContractChunks: chunkDetails.length,
      homeChunkContractInitialChunks: chunkInitialCount,
      homeChunkContractMobileDeferredChunks: chunkMobileDeferredCount,
      homeChunkContractSeoPreservedChunks: chunkSeoPreservedCount,
      homeChunkContractFetchableChunks: chunkFetchableCount,
      homeChunkContractHeightStableChunks: chunkHeightStableCount,
      homeChunkDetails: chunkDetails,
      homeChunkFetchPocState: chunkFetchPoc.state || "pending",
      homeChunkFetchPocTarget: chunkFetchPoc.targetChunkId || "home-close",
      homeChunkFetchPocUrl: chunkFetchPoc.url || "",
      homeChunkFetchPocMounted: Boolean(chunkFetchPoc.mounted),
      homeChunkFetchPocError: chunkFetchPoc.error || "",
      homeChunkFetchPocErrorState: chunkFetchPoc.state === "error",
      homeChunkFetchPocReady: chunkFetchPoc.state === "mounted" && Boolean(chunkFetchPoc.mounted) && !chunkFetchPoc.error,
      homeChunkFetchPocFetchedBytes: chunkFetchPoc.fetchedBytes || 0,
      homeChunkFetchPocMountedSections: chunkFetchPoc.mountedSections || 0,
      homeChunkFetchPocMountedNodes: chunkFetchPoc.mountedNodes || 0,
      homeChunkFetchPocRestoredCount: chunkFetchPoc.restoredCount || 0,
      homeChunkFetchPocRequestedAt: chunkFetchPoc.requestedAt || "",
      homeChunkFetchPocMountedAt: chunkFetchPoc.mountedAt || "",
      homeChunkFetchPocRestoredAt: chunkFetchPoc.restoredAt || "",
      homeChunkFetchManagerEnabled: Boolean(chunkFetchManager.enabled),
      homeChunkFetchManagerViewportActive: Boolean(chunkFetchManager.viewportActive),
      homeChunkFetchManagerState: chunkFetchManager.state || "idle",
      homeChunkFetchManagerRequestedChunks: chunkFetchManager.requestedChunks || 0,
      homeChunkFetchManagerFetchingChunks: chunkFetchManager.fetchingChunks || 0,
      homeChunkFetchManagerFetchedChunks: chunkFetchManager.fetchedChunks || 0,
      homeChunkFetchManagerMountedChunks: chunkFetchManager.mountedChunks || 0,
      homeChunkFetchManagerOffloadedChunks: chunkFetchManager.offloadedChunks || 0,
      homeChunkFetchManagerRestoredChunks: chunkFetchManager.restoredChunks || 0,
      homeChunkFetchManagerRepeatedNetworkRequests: chunkFetchManager.repeatedNetworkRequests || 0,
      homeChunkFetchManagerErrorChunks: chunkFetchManager.errorChunks || 0,
      homeChunkFetchManagerTotalFetchedBytes: chunkFetchManager.totalFetchedBytes || 0,
      homeChunkFetchManagerSourceNetworkRequests: chunkFetchManager.sourceNetworkRequests || 0,
      homeChunkFetchManagerSourceNetworkBytes: chunkFetchManager.sourceNetworkBytes || 0,
      homeChunkFetchManagerExtractedPayloadBytes: chunkFetchManager.extractedPayloadBytes || 0,
      homeChunkFetchManagerAvoidedFullPageRequests: chunkFetchManager.avoidedFullPageRequests || 0,
      homeChunkFetchManagerPayloadReductionEstimatePercent: chunkFetchManager.payloadReductionEstimatePercent || 0,
      homeChunkFetchManagerPayloadSourceState: chunkFetchManager.payloadSourceState || "idle",
      homeChunkFetchManagerPayloadSourceError: chunkFetchManager.payloadSourceError || "",
      homeChunkFetchManagerTotalMountedNodes: chunkFetchManager.totalMountedNodes || 0,
      homeChunkFetchManagerLastActionAt: chunkFetchManager.lastActionAt || "",
      homeChunkStableMobileViewportHeight: state.stableMobileViewportHeight || 0,
      homeChunkHydrateAheadViewports: getChunkFetchManagerConfig().hydrateAheadViewports,
      homeChunkOffloadViewports: getChunkFetchManagerConfig().offloadViewports,
      homeChunkRestoreBehindViewports: getChunkFetchManagerConfig().restoreBehindViewports,
      homeChunkCompactAheadViewports: getChunkFetchManagerConfig().compactAheadViewports,
      phase114StableThresholdsPass: !mobileReductionViewport || (
        Boolean(state.stableMobileViewportHeight) &&
        getChunkFetchManagerConfig().hydrateAheadViewports > 1.5 &&
        getChunkFetchManagerConfig().offloadViewports > getChunkFetchManagerConfig().hydrateAheadViewports
      ),
      homeChunkHeightReservationEnabled: Boolean(chunkHeightReservations.enabled),
      homeChunkHeightReservationViewportActive: Boolean(chunkHeightReservations.viewportActive),
      homeChunkHeightReservationMeasuredChunks: chunkHeightReservations.measuredChunks || 0,
      homeChunkHeightReservationEstimatedChunks: chunkHeightReservations.estimatedChunks || 0,
      homeChunkHeightReservationStableChunks: chunkHeightReservations.stableChunks || 0,
      homeChunkHeightReservationUnstableChunks: chunkHeightReservations.unstableChunks || 0,
      homeChunkHeightReservationTotalReservedHeight: chunkHeightReservations.totalReservedHeight || 0,
      homeChunkHeightReservationTotalMeasuredHeight: chunkHeightReservations.totalMeasuredHeight || 0,
      homeChunkHeightReservationMaxDelta: chunkHeightReservations.maxHeightDelta || 0,
      homeChunkHeightReservationScrollCompensations: chunkHeightReservations.scrollCompensations || 0,
      homeChunkHeightReservationScrollCompensationPixels: Math.round(chunkHeightReservations.scrollCompensationPixels || 0),
      homeChunkHeightReservationLastAdjustmentAt: chunkHeightReservations.lastAdjustmentAt || "",
      homeChunkActiveReservationExpectedChunks: chunkHeightReservations.activeReservationExpectedChunks || 0,
      homeChunkHeightReservationActiveReleasedChunks: chunkHeightReservations.activeReservationReleasedChunks || 0,
      homeChunkHeightReservationAppliedChunks: chunkHeightReservations.appliedReservationChunks || 0,
      homeChunkHeightReservationCleanContractSkippedChunks: chunkHeightReservations.cleanContractSkippedChunks || 0,
      homeChunkActiveReservationExpectedChunkIds: chunkHeightReservations.activeReservationExpectedChunkIds || [],
      homeChunkActiveReservationReleasedChunkIds: chunkHeightReservations.activeReservationReleasedChunkIds || [],
      homeChunkHeightReservationAppliedChunkIds: chunkHeightReservations.appliedReservationChunkIds || [],
      homeChunkHeightReservationCleanContractSkippedChunkIds: chunkHeightReservations.cleanContractSkippedChunkIds || [],
      homeChunkEducationActiveReservationReleased: Boolean(chunkHeightReservations.chunks?.["home-education"]?.activeReservationReleased),
      homeChunkEducationReservationApplied: Boolean(chunkHeightReservations.chunks?.["home-education"]?.appliedToLayout),
      homeChunkActiveReservationReleasePass: !mobileReductionViewport || (
        (chunkHeightReservations.activeReservationExpectedChunks || 0) ===
        (chunkHeightReservations.activeReservationReleasedChunks || 0)
      ),
      homeChunkProductDiscoveryActiveReservationReleased: Boolean(chunkHeightReservations.chunks?.["home-product-discovery"]?.activeReservationReleased),
      homeChunkProductDiscoveryReservationApplied: Boolean(chunkHeightReservations.chunks?.["home-product-discovery"]?.appliedToLayout),
      homeChunkProductDiscoveryActiveReleasePass: !mobileReductionViewport || (
        productDiscoveryActive === 0 ||
        Boolean(chunkHeightReservations.chunks?.["home-product-discovery"]?.activeReservationReleased)
      ),
      homeChunkHeightReservationPass: !mobileReductionViewport || (
        Boolean(chunkHeightReservations.enabled) &&
        Boolean(chunkHeightReservations.viewportActive) &&
        (chunkHeightReservations.unstableChunks || 0) === 0 &&
        (chunkHeightReservations.totalReservedHeight || 0) > 0
      ),
      chunkArtifactCompactionEnabled: Boolean(state.chunkArtifactCompaction.enabled),
      chunkArtifactCompactionCurrentSections: state.chunkArtifactCompaction.currentCompactedSections || 0,
      chunkArtifactCompactionCurrentNodes: state.chunkArtifactCompaction.currentCompactedNodes || 0,
      chunkArtifactCompactionCurrentScriptStyleNodes: state.chunkArtifactCompaction.currentCompactedScriptStyleNodes || 0,
      chunkArtifactCompactionTotalSections: state.chunkArtifactCompaction.totalCompactedSections || 0,
      chunkArtifactCompactionTotalNodes: state.chunkArtifactCompaction.totalCompactedNodes || 0,
      chunkArtifactCompactionTotalScriptStyleNodes: state.chunkArtifactCompaction.totalCompactedScriptStyleNodes || 0,
      chunkArtifactCompactionRestoredSections: state.chunkArtifactCompaction.totalRestoredSections || 0,
      chunkArtifactCompactionRestoredNodes: state.chunkArtifactCompaction.totalRestoredNodes || 0,
      chunkArtifactCompactionRestoredScriptStyleNodes: state.chunkArtifactCompaction.totalRestoredScriptStyleNodes || 0,
      chunkArtifactCompactionLastActionAt: state.chunkArtifactCompaction.lastActionAt || "",
      mediaCleanupEnabled: config.imageMemoryCleanup !== false,
      mediaCleanupCurrentStrippedSections: state.mediaCleanup.currentStrippedSections || 0,
      mediaCleanupCurrentStrippedMedia: state.mediaCleanup.currentStrippedMedia || 0,
      mediaCleanupTotalStrippedSections: state.mediaCleanup.totalStrippedSections || 0,
      mediaCleanupTotalStrippedMedia: state.mediaCleanup.totalStrippedMedia || 0,
      mediaCleanupTotalRestoredSections: state.mediaCleanup.totalRestoredSections || 0,
      mediaCleanupTotalRestoredMedia: state.mediaCleanup.totalRestoredMedia || 0,
      mediaCleanupProtectedSections: state.mediaCleanup.protectedSections || 0,
      mediaCleanupLastActionAt: state.mediaCleanup.lastActionAt || "",
      phase11MediaCleanupPass: config.imageMemoryCleanup === false || (
        (state.mediaCleanup.currentStrippedSections || 0) >= 0 &&
        (state.mediaCleanup.currentStrippedMedia || 0) >= 0
      ),
      scrollAnchorRepairEnabled: Boolean(state.scrollAnchorRepair.enabled),
      scrollAnchorRepairBatches: state.scrollAnchorRepair.batches || 0,
      scrollAnchorRepairCompensatedBatches: state.scrollAnchorRepair.compensatedBatches || 0,
      scrollAnchorRepairSkippedBatches: state.scrollAnchorRepair.skippedBatches || 0,
      scrollAnchorRepairUpwardBatches: state.scrollAnchorRepair.upwardBatches || 0,
      scrollAnchorRepairUpwardCompensatedBatches: state.scrollAnchorRepair.upwardCompensatedBatches || 0,
      scrollAnchorRepairMissingAnchorBatches: state.scrollAnchorRepair.missingAnchorBatches || 0,
      scrollAnchorRepairCompensationPixels: Math.round(state.scrollAnchorRepair.compensationPixels || 0),
      scrollAnchorRepairLastActionAt: state.scrollAnchorRepair.lastActionAt || "",
      phase111ScrollAnchorRepairPass: state.scrollAnchorRepair.enabled === false || (
          (state.scrollAnchorRepair.upwardBatches || 0) === 0 ||
          (state.scrollAnchorRepair.upwardCompensatedBatches || 0) > 0
        ),
      scrollIntegrityEnabled: (isPlainObject(config.scrollIntegrity) ? config.scrollIntegrity : DEFAULT_CONFIG.scrollIntegrity).enabled !== false,
      scrollIntegrityMonitorOnly: (isPlainObject(config.scrollIntegrity) ? config.scrollIntegrity : DEFAULT_CONFIG.scrollIntegrity).monitorOnly !== false,
      scrollIntegrityCorrections: state.scrollIntegrity.corrections || 0,
      scrollIntegrityBlockedJumps: state.scrollIntegrity.blockedJumps || 0,
      scrollIntegritySuspectedJumps: state.scrollIntegrity.suspectedJumps || 0,
      scrollIntegrityAllowedLargeJumps: state.scrollIntegrity.allowedLargeJumps || 0,
      scrollIntegrityLargestBlockedDelta: state.scrollIntegrity.largestBlockedDelta || 0,
      scrollIntegrityLastDelta: state.scrollIntegrity.lastDelta || 0,
      scrollIntegrityLastInputType: state.scrollIntegrity.lastInputType || "",
      scrollIntegrityLastActionAt: state.scrollIntegrity.lastActionAt || "",
      phase112ScrollIntegrityPass: state.scrollIntegrity.enabled === false || Boolean(state.scrollIntegrity.enabled),
      seoShellCompactionEnabled: Boolean(state.seoShellCompaction.enabled),
      seoShellCompactionCurrentSections: state.seoShellCompaction.currentCompactedSections || 0,
      seoShellCompactionCurrentOriginalNodes: state.seoShellCompaction.currentOriginalNodes || 0,
      seoShellCompactionCurrentCompactNodes: state.seoShellCompaction.currentCompactNodes || 0,
      seoShellCompactionCurrentSavedNodes: Math.max((state.seoShellCompaction.currentOriginalNodes || 0) - (state.seoShellCompaction.currentCompactNodes || 0), 0),
      seoShellCompactionTotalSections: state.seoShellCompaction.totalCompactedSections || 0,
      seoShellCompactionTotalOriginalNodes: state.seoShellCompaction.totalOriginalNodes || 0,
      seoShellCompactionTotalCompactNodes: state.seoShellCompaction.totalCompactNodes || 0,
      seoShellCompactionTotalSavedNodes: Math.max((state.seoShellCompaction.totalOriginalNodes || 0) - (state.seoShellCompaction.totalCompactNodes || 0), 0),
      seoShellCompactionRestoredSections: state.seoShellCompaction.totalRestoredSections || 0,
      seoShellCompactionRestoredNodes: state.seoShellCompaction.totalRestoredNodes || 0,
      seoShellCompactionLastActionAt: state.seoShellCompaction.lastActionAt || "",
      seoShellCompactionMobileDisabled: mobileReductionViewport,
      seoShellCompactionMobileProtectedSections: mobileSeoShellProtectedSections.length,
      seoShellCompactionMobileProtectedChunks: mobileSeoShellProtectedChunkIds.length,
      seoShellCompactionMobileProtectedChunkIds: mobileSeoShellProtectedChunkIds,
      seoShellCompactionMobileProtectedCompactedSections: mobileSeoShellProtectedCompactedSections,
      phase115MobileSeoShellProtectionPass: !mobileReductionViewport || (
        mobileSeoShellProtectedCompactedSections === 0 &&
        initialDomNodes <= mobileReductionSettings.domNodeBudget
      ),
      phase116HydrateAheadViewports: getChunkFetchManagerConfig().hydrateAheadViewports,
      phase116OffloadViewports: getChunkFetchManagerConfig().offloadViewports,
      phase116VisiblePathProtectedSections: mobileVisiblePathProtectedSections.length,
      phase116VisiblePathProtectedChunks: mobileVisiblePathProtectedChunkIds.length,
      phase116VisiblePathProtectedChunkIds: mobileVisiblePathProtectedChunkIds,
      phase116VisiblePathProtectedShellSections: mobileVisiblePathProtectedShellSections.length,
      phase116VisiblePathProtectedShellSectionIds: mobileVisiblePathProtectedShellSections.map((section) => section.id || section.name || section.homeChunkId).filter(Boolean),
      phase116MobileVisiblePathPass: !mobileReductionViewport || (
        getChunkFetchManagerConfig().hydrateAheadViewports >= 3 &&
        getChunkFetchManagerConfig().offloadViewports >= 4 &&
        mobileVisiblePathProtectedShellSections.length === 0
      ),
      mobileStableVisualSections: controlResult.mobileStableVisualSections || 0,
      mobileStableVisualMountedSlots: controlResult.mobileStableVisualMountedSlots || 0,
      mobileStableVisualHeightLocksReleased: controlResult.mobileStableVisualHeightLocksReleased || 0,
      cleanVisualOnlySections: controlResult.cleanVisualOnlySections || 0,
      cleanVisualOnlyHeightLocksReleased: controlResult.cleanVisualOnlyHeightLocksReleased || 0,
      mobileStableVisualActiveSections: mobileStableVisualSections.length,
      mobileStableVisualProblemSections: mobileStableVisualProblemSections.length,
      mobileStableVisualProblemSectionIds: mobileStableVisualProblemSections.map((section) => section.id || section.name || section.homeChunkId).filter(Boolean),
      featuredRepeatDeliveryMobileStablePass: !mobileReductionViewport || (
        mobileStableVisualSections.length >= 1 &&
        mobileStableVisualProblemSections.length === 0
      ),
      repeatDeliveryCleanVirtualizationSections: repeatDeliverySections.length,
      repeatDeliveryVisualOnlySections: repeatDeliveryVisualOnlySections.length,
      repeatDeliveryHeightLockedSections: repeatDeliveryHeightLockedSections.length,
      repeatDeliveryHeightLockedSectionIds: repeatDeliveryHeightLockedSections.map((section) => section.id || section.name || section.homeChunkId).filter(Boolean),
      repeatDeliveryChunkReservationApplied,
      repeatDeliveryChunkReservationSkipped,
      repeatDeliveryCleanVirtualizationPass: !mobileReductionViewport || (
        repeatDeliverySections.length >= 1 &&
        repeatDeliveryVisualOnlySections.length >= 1 &&
        repeatDeliveryHeightLockedSections.length === 0 &&
        repeatDeliveryChunkReservationApplied === false &&
        repeatDeliveryChunkReservationSkipped === true
      ),
      homeChunkFetchRequests: controlResult.chunkFetchRequests || 0,
      homeChunkHydrateRequests: controlResult.chunkHydrateRequests || 0,
      homeChunkOffloadRequests: controlResult.chunkOffloadRequests || 0,
      homeChunkRealManagerPass: !mobileReductionViewport || (
        Boolean(chunkFetchManager.enabled) &&
        Boolean(chunkFetchManager.viewportActive) &&
        (chunkFetchManager.errorChunks || 0) === 0 &&
        (chunkFetchManager.repeatedNetworkRequests || 0) === 0
      ),
      homeChunkLightweightPayloadPass: !mobileReductionViewport || (
        Boolean(chunkFetchManager.enabled) &&
        (chunkFetchManager.sourceNetworkRequests || 0) <= 1 &&
        (chunkFetchManager.repeatedNetworkRequests || 0) === 0 &&
        (chunkFetchManager.payloadSourceError || "") === "" &&
        (chunkFetchManager.fetchedChunks || 0) >= 1
      ),
      homeChunkDesktopUnchanged: mobileReductionViewport ? true : (
        (chunkFetchManager.requestedChunks || 0) === 0 &&
        (chunkFetchManager.fetchedChunks || 0) === 0
      ),
      productDiscoveryChunkConfigured: Boolean(productDiscoveryChunk),
      productDiscoveryChunkFetchable: Boolean(productDiscoveryChunk?.fetchable),
      productDiscoveryChunkSections: productDiscoverySections.length,
      productDiscoveryChunkActive: productDiscoveryActive,
      productDiscoveryChunkShell: productDiscoveryShell,
      productDiscoveryChunkCrawlableLinks: productDiscoveryChunk?.crawlableLinks || 0,
      productDiscoveryChunkCrawlableTextChars: productDiscoveryChunk?.crawlableTextChars || 0,
      productDiscoveryMobileChunkEligible: productDiscoveryMobileEligible,
      productDiscoveryChunkProtected: mobileReductionSettings.protectChunkIds.includes("home-product-discovery"),
      initialDomNodes,
      mountedChunks,
      fetchedChunks,
      serverInitialHtmlReductionActive: false,
      clientMobileStartupReductionActive: mobileReductionSettings.enabled && mobileReductionViewport,
      mobileInitialHtmlReductionEnabled: mobileReductionSettings.enabled,
      mobileInitialHtmlReductionViewport: mobileReductionViewport,
      mobileInitialHtmlReductionMaxWidth: mobileReductionSettings.maxWidth,
      mobileInitialHtmlReductionLowerChunkMinOrder: mobileReductionSettings.lowerChunkMinOrder,
      mobileInitialHtmlReductionProtectedSections: mobileProtectedSections.length,
      mobileInitialHtmlReductionHeroProtected: mobileProtectedSections.some((section) => {
        return section.type === "critical" || section.homeChunkId === "home-initial";
      }) || Boolean(controlResult.mobileInitialReductionHeroProtected),
      mobileInitialHtmlReductionEligibleSections: mobileLowerSections.length,
      mobileInitialHtmlReductionActiveLowerSections: mobileLowerActiveSections,
      mobileInitialHtmlReductionShellLowerSections: mobileLowerShellSections,
      mobileInitialHtmlReductionVirtualizedThisSweep: controlResult.mobileInitialReductionVirtualized || 0,
      mobileInitialHtmlReductionDesktopUnchanged: !mobileReductionViewport,
      mobileLaunchDomNodeBudget: mobileReductionSettings.domNodeBudget,
      mobileLaunchActiveSectionBudget: mobileReductionSettings.activeSectionBudget,
      mobileLaunchDomBudgetPass: !mobileReductionViewport || initialDomNodes <= mobileReductionSettings.domNodeBudget,
      mobileLaunchActiveSectionBudgetPass: !mobileReductionViewport || activeManaged <= mobileReductionSettings.activeSectionBudget,
      mobileLaunchFarManagedPass: farManagedActive === 0,
      mobileLaunchBudgetPass,
      controlActivationEnabled: Boolean(controlResult.enabled),
      startupSweepComplete: state.startupSweepComplete,
      scrollVelocity: Math.round(state.scrollVelocity * 100) / 100,
      reducedMotion: window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches || false,
      shopifyDesignMode: Boolean(window.Shopify?.designMode),
      hydrateRequests: controlResult.hydrateRequests,
      virtualizeRequests: controlResult.virtualizeRequests,
      skippedHydrateRequests: controlResult.skippedHydrateRequests,
      pressureSkippedHydrateRequests: controlResult.pressureSkippedHydrateRequests,
      nearestUpcomingId: controlResult.nearestUpcomingId,
      lifecycle
    };
  }

  function stripRoots(sections) {
    return sections.map(({ root, ...section }) => ({ ...section }));
  }

  function dispatchRefresh() {
    window.dispatchEvent(new CustomEvent("eom:page-window-manager-refresh", {
      detail: getSnapshot()
    }));
  }

  function refresh() {
    if (refreshInProgress) return getSnapshot();
    refreshInProgress = true;
    refreshFrame = null;
    window.clearTimeout(refreshTimer);

    try {
      state.enabled = shouldRun();
      state.lastRefreshAt = new Date().toISOString();
      state.lastRefreshError = "";
      state.config = getPublicConfig();

      const roots = Array.from(document.querySelectorAll(config.selector)).filter((root) => {
        return !root.closest("[data-eom-home-chunk-fetch-poc-mount]") &&
          !root.closest("[data-eom-home-chunk-cache-mount]");
      });
      const cleanVisualOnlyResult = enforceCleanVisualOnlyRoots(roots);
      const stableVisualResult = stabilizeMobileVisualRoots(roots);
      const sections = roots.map(getSectionSnapshot);
      rememberLifecycle(sections);
      const controlResult = applyControlledActivation(sections);
      const controlledSections = controlResult.hydrateRequests > 0 || controlResult.virtualizeRequests > 0
        ? roots.map(getSectionSnapshot)
        : sections;
      const chunkControlResult = evaluateHomeChunkFetchManager(controlledSections);
      const chunkControlledSections = chunkControlResult.hydrateRequests > 0 || chunkControlResult.offloadRequests > 0
        ? roots.map(getSectionSnapshot)
        : controlledSections;
      const heightReservationResult = evaluateChunkHeightReservations(chunkControlledSections);
      const heightControlledSections = heightReservationResult.viewportActive
        ? roots.map(getSectionSnapshot)
        : chunkControlledSections;

      if (
        controlledSections !== sections ||
        chunkControlledSections !== controlledSections ||
        heightControlledSections !== chunkControlledSections
      ) {
        rememberLifecycle(heightControlledSections);
      }

      state.sections = stripRoots(heightControlledSections);
      state.chunkPlaceholders = getChunkPlaceholderModels(state.sections);
      renderChunkPlaceholderPreview(state.chunkPlaceholders);
      state.summary = buildSummary(state.sections, {
        ...controlResult,
        chunkFetchRequests: chunkControlResult.fetchRequests,
        chunkHydrateRequests: chunkControlResult.hydrateRequests,
        chunkOffloadRequests: chunkControlResult.offloadRequests,
        chunkHeightMeasured: heightReservationResult.measuredChunks,
        chunkHeightEstimated: heightReservationResult.estimatedChunks,
        chunkHeightStable: heightReservationResult.stableChunks,
        chunkHeightUnstable: heightReservationResult.unstableChunks,
        chunkHeightScrollCompensations: heightReservationResult.scrollCompensations,
        chunkHeightScrollCompensationPixels: heightReservationResult.scrollCompensationPixels,
        cleanVisualOnlySections: cleanVisualOnlyResult.cleanVisualOnlySections,
        cleanVisualOnlyHeightLocksReleased: cleanVisualOnlyResult.heightLocksReleased,
        mobileStableVisualSections: stableVisualResult.stabilizedSections,
        mobileStableVisualMountedSlots: stableVisualResult.mountedSlots,
        mobileStableVisualHeightLocksReleased: stableVisualResult.heightLocksReleased
      });
      state.initialized = true;

      if (config.debug) {
        console.groupCollapsed("EOM Page Window Manager Snapshot");
        console.table(state.summary);
        console.table(state.sections);
        console.groupEnd();
      }
    } catch (error) {
      state.lastRefreshError = error?.message || String(error);
      state.summary = {
        ...(state.summary || {}),
        refreshError: state.lastRefreshError,
        refreshErrorState: true,
        total: state.sections.length,
        homeChunksConfigured: Array.isArray(config.homeChunks) ? config.homeChunks.length : 0,
        homeChunksDiscovered: new Set(state.sections.map((section) => section.homeChunkId).filter(Boolean)).size
      };

      if (config.debug) {
        console.error("EOM Page Window Manager refresh failed", error);
      }
    } finally {
      refreshInProgress = false;
    }

    dispatchRefresh();
    return getSnapshot();
  }

  function scheduleRefresh() {
    const now = Date.now();
    const currentY = window.scrollY || 0;
    const elapsed = Math.max(now - state.lastScrollAt, 16);
    state.scrollVelocity = (currentY - state.lastScrollY) / elapsed;
    state.lastScrollY = currentY;
    state.lastScrollAt = now;

    if (refreshFrame) return;

    refreshFrame = window.requestAnimationFrame(() => {
      refreshTimer = window.setTimeout(refresh, toNumber(config.refreshDelay, DEFAULT_CONFIG.refreshDelay));
    });
  }

  function getSnapshot() {
    return {
      enabled: state.enabled,
      initialized: state.initialized,
      reason: state.reason,
      lastRefreshAt: state.lastRefreshAt,
      lastRefreshError: state.lastRefreshError,
      config: getPublicConfig(),
      summary: { ...state.summary },
      lifecycle: state.lifecycle.map((event) => ({ ...event })),
      chunkPlaceholders: state.chunkPlaceholders.map((placeholder) => ({ ...placeholder })),
      chunkFetchPoc: { ...state.chunkFetchPoc },
      chunkFetchManager: {
        ...state.chunkFetchManager,
        chunks: Object.fromEntries(
          Object.entries(state.chunkFetchManager.chunks || {}).map(([key, record]) => [key, { ...record }])
        )
      },
      chunkHeightReservations: {
        ...state.chunkHeightReservations,
        chunks: Object.fromEntries(
          Object.entries(state.chunkHeightReservations.chunks || {}).map(([key, record]) => [key, {
            ...record,
            sectionHeights: { ...(record.sectionHeights || {}) }
          }])
        )
      },
      chunkArtifactCompaction: { ...state.chunkArtifactCompaction },
      mediaCleanup: { ...state.mediaCleanup },
      scrollAnchorRepair: { ...state.scrollAnchorRepair },
      scrollIntegrity: { ...state.scrollIntegrity },
      seoShellCompaction: { ...state.seoShellCompaction },
      sections: state.sections.map((section) => ({ ...section }))
    };
  }

  function updateConfig(nextConfig = {}) {
    config = mergeConfig(config, nextConfig);
    window.EOMPageWindowManagerConfig = {
      ...(window.EOMPageWindowManagerConfig || {}),
      ...nextConfig
    };
    return refresh();
  }

  function setChunkPlaceholderPreview(enabled) {
    return updateConfig({ chunkPlaceholderPreview: Boolean(enabled) });
  }

  function hydrateSection(rootOrSelector) {
    const root = typeof rootOrSelector === "string" ? document.querySelector(rootOrSelector) : rootOrSelector;
    return window.EOMVirtualSections?.hydrate?.(root) || false;
  }

  function virtualizeSection(rootOrSelector) {
    const root = typeof rootOrSelector === "string" ? document.querySelector(rootOrSelector) : rootOrSelector;
    return window.EOMVirtualSections?.virtualize?.(root) || false;
  }

  function hydrateTopSections() {
    const sections = Array.from(document.querySelectorAll(config.selector)).map(getSectionSnapshot);
    const topSections = sections
      .filter((section) => section.managedByWindow && (section.type === "critical" || section.index <= 1))
      .sort((a, b) => a.index - b.index)
      .slice(0, 2);

    let hydrated = 0;

    topSections.forEach((section) => {
      if (window.EOMVirtualSections?.hydrate?.(section.root)) {
        hydrated += 1;
        logLifecycle("back-to-top-hydrate-requested", section);
      }
    });

    return hydrated;
  }

  function init() {
    state.enabled = shouldRun();
    state.initialized = true;

    refresh();
    window.setTimeout(scheduleRefresh, 0);
    window.setTimeout(scheduleRefresh, 250);
    window.setTimeout(scheduleRefresh, 900);

    window.addEventListener("scroll", () => {
      handleScrollIntegrity();
      scheduleRefresh();
    }, { passive: true });
    window.addEventListener("resize", scheduleRefresh, { passive: true });
    window.addEventListener("load", scheduleRefresh, { once: true, passive: true });

    window.addEventListener("wheel", () => recordScrollInput("wheel"), { passive: true, capture: true });
    window.addEventListener("touchstart", () => recordScrollInput("touchstart"), { passive: true, capture: true });
    window.addEventListener("touchmove", () => recordScrollInput("touchmove"), { passive: true, capture: true });
    window.addEventListener("keydown", (event) => {
      const scrollKeys = ["ArrowDown", "ArrowUp", "PageDown", "PageUp", "Home", "End", " "];
      if (scrollKeys.includes(event.key)) recordScrollInput("key");
    }, { capture: true });

    document.addEventListener("click", (event) => {
      if (isExplicitScrollTrigger(event.target)) {
        allowExplicitScrollIntent();
      }
    }, { capture: true });

    document.addEventListener("shopify:section:load", scheduleRefresh);
    document.addEventListener("shopify:section:unload", scheduleRefresh);
    document.addEventListener("shopify:section:select", scheduleRefresh);
    document.addEventListener("shopify:section:deselect", scheduleRefresh);

    window.addEventListener("eom:section-hydrated", scheduleRefresh);
    window.addEventListener("eom:section-virtualized", scheduleRefresh);

    if (config.hydrateOnUserIntent !== false) {
      document.addEventListener("pointerdown", (event) => {
        const root = event.target.closest?.(config.selector);
        if (root) hydrateSection(root);
      }, { capture: true, passive: true });

      document.addEventListener("focusin", (event) => {
        const root = event.target.closest?.(config.selector);
        if (root) hydrateSection(root);
      }, { capture: true });

      document.addEventListener("click", (event) => {
        const trigger = event.target.closest?.(config.backToTopSelector);
        if (trigger) hydrateTopSections();
      }, { capture: true });
    }

    const chunkManagerConfig = getChunkFetchManagerConfig();
    if (chunkManagerConfig.autoStart && chunkManagerConfig.enabled && !window.Shopify?.designMode) {
      window.setTimeout(scheduleRefresh, 650);
    }
  }

  window.EOMPageWindowManager = {
    getSnapshot,
    refresh,
    scheduleRefresh,
    updateConfig,
    setChunkPlaceholderPreview,
    startHomeChunkFetchPoc,
    fetchHomeChunk,
    evaluateHomeChunkFetchManager,
    hydrateSection,
    virtualizeSection,
    hydrateTopSections,
    config,
    state
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();